import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import JSZip from "jszip";
import { toast } from "sonner";
import { useModalities } from "@/contexts/ModalitiesContext";
import { useUploadProgress, UPLOAD_EXTRACT_TOAST_ID } from "@/contexts/UploadProgressContext";
import { useRbac } from "@/hooks/useRbac";
import {
  acceptsOnlyZip,
  buildAcceptAttribute,
  formatAcceptedExtensions,
  getSizeValidationError,
  getValidationError,
  inferFileType,
  isOtherModality as modalityIsOther,
} from "@/lib/modalities";
import { formatApiErrorDetail } from "@/lib/api";
import { formatPatientDisplayUhid } from "@/lib/patientDisplay";
import {
  buildEchoMeasurements,
  emptyEchoMeasurementValues,
  isEchoFormModality,
  isEchoMeasurementValuesEmpty,
} from "@/lib/echoMeasurements";
import {
  needsStandaloneNewPatientReview,
  standaloneNewPatientMismatchPatch,
} from "@/lib/uploadMismatch";
import { isSamePatientAsLocked } from "@/lib/addStudyMismatch";
import ModalityDotLabel from "@/components/ModalityDotLabel";
import EchoMeasurementForm from "@/components/EchoMeasurementForm";
import UploadShareTargetFields from "@/components/UploadShareTargetFields";
import {
  ANATOMY_TYPE_1_OPTIONS,
  anatomyFromExtractDetails,
  buildAnatomyPayload,
  getAnatomyType2Options,
  getAnatomyType3Options,
  needsAnatomyType2,
  needsAnatomyType3,
  normalizeAnatomyType2,
  validateAnatomyTypes,
} from "@/lib/anatomyTypes";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  X, CloudUpload, Search, User, ChevronDown,
  FolderInput, Loader2, UserPlus, Save, AlertTriangle, FileText,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const uploadLabelClass = "text-xs text-black";
const uploadInputClass = "border-[var(--xl-line2)] bg-[var(--xl-surf)] text-black placeholder:text-black";
const uploadSelectClass = "border-[var(--xl-line2)] bg-[var(--xl-surf)] text-black data-[placeholder]:text-black";
const uploadSelectContentClass = "text-black";

const UPLOAD_MODALITY_KEYS = ["ct_images", "3mensio_ct"];

/** Modalities that require anatomy type + CT reviewer when creating a study. */
const CT_STUDY_FIELD_MODALITIES = new Set(["3mensio_ct", "ct_report"]);
const modalityRequiresCtStudyFields = (key) => CT_STUDY_FIELD_MODALITIES.has(key);

const snapshotShareTargetUser = (user) => {
  if (!user?.id) return null;
  return {
    id: user.id,
    name: user.name || "",
    email: user.email || "",
    organization_id: user.organization_id || "",
  };
};

const snapshotShareTargetUsers = (users) => {
  const list = Array.isArray(users) ? users : (users?.id ? [users] : []);
  const seen = new Set();
  return list
    .map((user) => snapshotShareTargetUser(user))
    .filter((user) => {
      if (!user?.id || seen.has(user.id)) return false;
      seen.add(user.id);
      return true;
    });
};

const shareTargetsFromFile = (file) => snapshotShareTargetUsers(
  file?.share_target_users ?? file?.share_target_user,
);

const EMPTY_ANATOMY_FIELDS = {
  anatomy_type_1: "",
  anatomy_type_2: "",
  anatomy_type_3: "",
};

const anatomyFromPatient = (patient) => anatomyFromExtractDetails(
  patient?.anatomyDetails ?? patient?.anatomy_details,
);

const EMPTY_PATIENT_FORM = {
  external_uhid: "",
  name: "",
  age: "",
  gender: "Male",
  referring_doctor: "",
  institution_name: "",
};

/** Required fields for creating a patient from extract / form. */
const getMissingPatientFormFields = (form) => {
  const missing = [];
  if (!String(form?.external_uhid ?? "").trim()) missing.push("UHID");
  if (!String(form?.name ?? "").trim()) missing.push("Name");
  if (!String(form?.age ?? "").trim()) missing.push("Age");
  return missing;
};

const GENDER_FROM_EXTRACT = { F: "Female", M: "Male", O: "Other" };

const normalizeDateOfBirth = (rawDob) => {
  const value = String(rawDob ?? "").trim();
  if (/^\d{8}$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return undefined;
};

const patientFromExtractDetails = (details) => {
  if (!details?.system_uhid) return null;

  const genderKey = String(details.gender ?? "").trim().toUpperCase();
  const gender = GENDER_FROM_EXTRACT[genderKey] || details.gender || undefined;

  return {
    uhid: details.system_uhid,
    name: details.name || details.system_uhid,
    external_uhid: details.patient_id || undefined,
    gender,
    date_of_birth: normalizeDateOfBirth(details.date_of_birth),
  };
};

const ageFromDateOfBirth = (rawDob) => {
  const iso = normalizeDateOfBirth(rawDob);
  if (!iso) return "";

  const [yearStr, monthStr, dayStr] = iso.split("-");
  const birthDate = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
  if (Number.isNaN(birthDate.getTime())) return "";

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age >= 0 ? String(age) : "";
};

const normalizeCompareText = (value) =>
  String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");

const normalizeCompareId = (value) =>
  String(value ?? "").trim().toUpperCase().replace(/\s+/g, "");

const normalizeCompareGender = (value) => {
  const key = String(value ?? "").trim().toUpperCase();
  if (key === "M" || key === "MALE") return "male";
  if (key === "F" || key === "FEMALE") return "female";
  if (key === "O" || key === "OTHER") return "other";
  return normalizeCompareText(value);
};

/** Build a comparable identity from DICOM extract `patient_details`. */
const identityFromExtractDetails = (details) => {
  if (!details) return null;
  const genderKey = String(details.gender ?? "").trim().toUpperCase();
  const gender = GENDER_FROM_EXTRACT[genderKey] || details.gender || "";
  const age = details.age != null && details.age !== ""
    ? String(details.age)
    : ageFromDateOfBirth(details.date_of_birth);

  return {
    uhid: details.system_uhid || "",
    external_uhid: details.patient_id || "",
    name: details.name || "",
    gender,
    age,
    institution_name: String(details.institution_name ?? "").trim(),
  };
};

const identityFromPatient = (patient) => {
  if (!patient) return null;
  return {
    uhid: patient.uhid || "",
    external_uhid: patient.external_uhid || "",
    name: patient.name || "",
    gender: patient.gender || "",
    age: patient.age != null && patient.age !== "" ? String(patient.age) : "",
    institution_name: patient.institution_name || "",
  };
};

/**
 * Compare name, age, and gender against extract details.
 * UHID is ignored. Empty list = match / nothing to compare.
 */
const getPatientDetailMismatches = (currentPatient, extractDetails) => {
  const current = identityFromPatient(currentPatient);
  const extracted = identityFromExtractDetails(extractDetails);
  if (!current || !extracted) return [];

  const mismatches = [];

  if (
    normalizeCompareText(current.name)
    && normalizeCompareText(extracted.name)
    && normalizeCompareText(current.name) !== normalizeCompareText(extracted.name)
  ) {
    mismatches.push({
      field: "Name",
      current: current.name || "—",
      extracted: extracted.name || "—",
    });
  }

  if (
    normalizeCompareGender(current.gender)
    && normalizeCompareGender(extracted.gender)
    && normalizeCompareGender(current.gender) !== normalizeCompareGender(extracted.gender)
  ) {
    mismatches.push({
      field: "Gender",
      current: current.gender || "—",
      extracted: extracted.gender || "—",
    });
  }

  if (
    current.age
    && extracted.age
    && String(current.age) !== String(extracted.age)
  ) {
    mismatches.push({
      field: "Age",
      current: current.age || "—",
      extracted: extracted.age || "—",
    });
  }

  return mismatches;
};

/** Side-by-side rows for existing system patient vs DICOM extract (exists dialog). */
const buildPatientCompareRows = (existingPatient, extractDetails) => {
  const current = identityFromPatient(existingPatient);
  const extracted = identityFromExtractDetails(extractDetails);
  if (!extracted) return [];

  const currentUhid = existingPatient
    ? formatPatientDisplayUhid(existingPatient)
    : (current?.external_uhid || current?.uhid || "—");
  const extractedUhid = extracted.external_uhid || extracted.uhid || "—";

  return [
    {
      field: "UHID",
      current: currentUhid || "—",
      extracted: extractedUhid,
    },
    {
      field: "Name",
      current: current?.name || "—",
      extracted: extracted.name || "—",
    },
    {
      field: "Age",
      current: current?.age || "—",
      extracted: extracted.age || "—",
    },
    {
      field: "Gender",
      current: current?.gender || "—",
      extracted: extracted.gender || "—",
    },
    {
      field: "Hospital",
      current: current?.institution_name || "—",
      extracted: extracted.institution_name || "—",
    },
  ];
};

const patientCompareValuesDiffer = (row) => {
  if (!row) return false;
  if (row.field === "UHID") {
    return normalizeCompareId(row.current) !== normalizeCompareId(row.extracted);
  }
  if (row.field === "Gender") {
    return normalizeCompareGender(row.current) !== normalizeCompareGender(row.extracted);
  }
  if (row.field === "Age") {
    return String(row.current ?? "") !== String(row.extracted ?? "");
  }
  return normalizeCompareText(row.current) !== normalizeCompareText(row.extracted);
};

/** Existing system patient referenced by extract details (when `exists` is true). */
const existingPatientFromExtractDetails = (details) => {
  if (details?.exists !== true) return null;
  const uhid = String(details.system_uhid ?? "").trim();
  if (!uhid) return null;
  const name = String(details.name ?? "").trim();
  const displayUhid = String(details.patient_id ?? "").trim() || uhid;
  return { uhid, name, displayUhid };
};

const newPatientFormFromExtractDetails = (details) => {
  if (!details) return { ...EMPTY_PATIENT_FORM };

  const genderKey = String(details.gender ?? "").trim().toUpperCase();
  const gender = GENDER_FROM_EXTRACT[genderKey] || details.gender || "Male";
  const age = details.age != null && details.age !== ""
    ? String(details.age)
    : ageFromDateOfBirth(details.date_of_birth);

  // Prefer system UHID when patient already exists; otherwise DICOM patient_id.
  const externalUhid = details.exists === true
    ? (details.system_uhid || details.patient_id || "")
    : (details.patient_id || details.system_uhid || "");

  return {
    external_uhid: externalUhid,
    name: details.name || "",
    age,
    gender,
    referring_doctor: String(details.physician_name ?? "").trim(),
    institution_name: String(details.institution_name ?? "").trim(),
  };
};

const applyExtractedPatientDetails = (patientDetails, {
  setPatientMode,
  setPatientUhid,
  setExtractedPatient,
  setNewPatientForm,
}) => {
  if (!patientDetails) return;

  // Always prefill the visible New Patient form (existing-patient UI is currently hidden).
  setNewPatientForm(newPatientFormFromExtractDetails(patientDetails));

  if (patientDetails.exists === true) {
    const extracted = patientFromExtractDetails(patientDetails);
    if (extracted?.uhid) {
      setPatientUhid(extracted.uhid);
      setExtractedPatient(extracted);
      setPatientMode("existing");
    }
    return;
  }

  if (patientDetails.exists === false) {
    setPatientMode("new");
    setPatientUhid("");
    setExtractedPatient(null);
    return;
  }

  const extracted = patientFromExtractDetails(patientDetails);
  if (extracted?.uhid) {
    setPatientUhid(extracted.uhid);
    setExtractedPatient(extracted);
    setPatientMode("existing");
  }
};

// Searchable patient combobox
function PatientCombobox({ value, onChange, initialPatient = null, selectedPatient = null }) {
  const [query, setQuery] = useState("");
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(() => (
    initialPatient?.uhid && initialPatient.uhid === value ? initialPatient : null
  ));
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/patients`, { params: { limit: 20 } })
      .then(res => setPatients(res.data.patients || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }

    const match = patients.find((p) => p.uhid === value);
    if (match) {
      setSelected(match);
      return;
    }

    if (selectedPatient?.uhid === value) {
      setSelected(selectedPatient);
    }

    let cancelled = false;
    axios.get(`${API}/patients/${encodeURIComponent(value)}`)
      .then((res) => {
        if (cancelled) return;
        const patient = res.data?.patient;
        if (patient?.uhid === value) setSelected(patient);
      })
      .catch(() => {
        if (!cancelled && selectedPatient?.uhid === value) {
          setSelected(selectedPatient);
        }
      });

    return () => { cancelled = true; };
  }, [value, patients, selectedPatient]);

  useEffect(() => {
    if (!selectedPatient?.uhid) return;
    setPatients((prev) => (
      prev.some((p) => p.uhid === selectedPatient.uhid) ? prev : [selectedPatient, ...prev]
    ));
  }, [selectedPatient]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      axios.get(`${API}/patients`, { params: { search: query, limit: 30 } })
        .then(res => setPatients(res.data.patients || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 300);
  }, [query]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (p) => {
    setSelected(p);
    onChange(p.uhid);
    setOpen(false);
    setQuery("");
  };

  const clear = (e) => {
    e.stopPropagation();
    setSelected(null);
    onChange("");
    setQuery("");
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        data-testid="patient-combobox-trigger"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 h-10 border border-[var(--xl-line2)] bg-[var(--xl-surf)] rounded-md text-sm text-left text-black hover:border-[var(--xl-primary)]/50 transition-colors"
      >
        <User className="w-4 h-4 text-black flex-shrink-0" />
        {selected ? (
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="font-medium truncate">{selected.name || selected.uhid}</span>
            <span className="text-[10px] text-black font-mono flex-shrink-0">{formatPatientDisplayUhid(selected)}</span>
          </div>
        ) : (
          <span className="flex-1 text-black">Search patient by name or UHID…</span>
        )}
        {selected ? (
          <X className="w-3.5 h-3.5 text-black hover:text-black" onClick={clear} />
        ) : (
          <ChevronDown className="w-4 h-4 text-black" />
        )}
      </button>

      {open && (
        <div className="absolute z-[100] top-full mt-1 w-full bg-white border border-[var(--xl-line)] rounded-lg shadow-lg overflow-hidden text-black">
          <div className="p-2 border-b border-[var(--xl-line)]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-black" />
              <input
                autoFocus
                data-testid="patient-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to search…"
                className="w-full h-8 pl-8 pr-3 text-sm text-black placeholder:text-black bg-[var(--xl-bg)] border border-[var(--xl-line)] rounded-md outline-none focus:border-[var(--xl-primary)]"
              />
            </div>
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {loading ? (
              <li className="px-3 py-6 text-xs text-black text-center">Loading…</li>
            ) : patients.length === 0 ? (
              <li className="px-3 py-6 text-xs text-black text-center">No patients found</li>
            ) : patients.map((p) => (
              <li key={p.uhid}>
                <button
                  type="button"
                  data-testid={`patient-option-${p.uhid}`}
                  onClick={() => select(p)}
                  className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-[var(--xl-primary-soft)] transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-[var(--xl-primary-soft)] flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-semibold text-black">{p.name?.[0] ?? "?"}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                      <p className="text-[11px] text-black font-mono">{formatPatientDisplayUhid(p)}</p>
                    </div>
                  </div>
                  {p.age && <span className="text-xs text-black">{p.age}y</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function NewPatientFormFields({ form, onChange, className = "" }) {
  return (
    <div
      className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.4fr)_minmax(0,0.4fr)_minmax(0,0.7fr)_minmax(0,1.2fr)_minmax(0,1.2fr)] gap-2 sm:gap-3 ${className}`}
    >
      <div className="space-y-1.5 min-w-0">
        <Label className={uploadLabelClass}>UHID *</Label>
        <Input
          data-testid="upload-input-uhid"
          value={form.external_uhid}
          onChange={(e) => onChange({ ...form, external_uhid: e.target.value })}
          placeholder="UHID-0007"
          className={`h-9 ${uploadInputClass}`}
        />
      </div>
      <div className="space-y-1.5 min-w-0">
        <Label className={uploadLabelClass}>Full Name *</Label>
        <Input
          data-testid="upload-input-name"
          value={form.name}
          onChange={(e) => onChange({ ...form, name: e.target.value })}
          placeholder="Patient name"
          className={`h-9 ${uploadInputClass}`}
        />
      </div>
      <div className="space-y-1.5 min-w-0">
        <Label className={uploadLabelClass}>Age *</Label>
        <Input
          data-testid="upload-input-age"
          type="number"
          value={form.age}
          onChange={(e) => onChange({ ...form, age: e.target.value })}
          placeholder="45"
          className={`h-9 ${uploadInputClass}`}
        />
      </div>
      <div className="space-y-1.5 min-w-0">
        <Label className={uploadLabelClass}>Gender *</Label>
        <Select
          value={form.gender}
          onValueChange={(v) => onChange({ ...form, gender: v })}
        >
          <SelectTrigger data-testid="upload-select-gender" className={`h-9 ${uploadSelectClass}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={uploadSelectContentClass}>
            <SelectItem value="Male">Male</SelectItem>
            <SelectItem value="Female">Female</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5 min-w-0">
        <Label className={uploadLabelClass}>Doctor</Label>
        <Input
          data-testid="upload-input-doctor"
          value={form.referring_doctor}
          onChange={(e) => onChange({ ...form, referring_doctor: e.target.value })}
          placeholder="Dr. ..."
          className={`h-9 ${uploadInputClass}`}
        />
      </div>
      <div className="space-y-1.5 min-w-0">
        <Label className={uploadLabelClass}>Hospital name</Label>
        <Input
          data-testid="upload-input-hospital"
          value={form?.institution_name ?? ""}
          onChange={(e) => onChange({ ...form, institution_name: e.target.value })}
          placeholder="Hospital name"
          className={`h-9 ${uploadInputClass}`}
        />
      </div>
    </div>
  );
}

export default function UploadPage({
  embeddedUhid = null,
  embeddedPatient = null,
  hidePatientControls = false,
  hideStudyControls = false,
  lockPatientUhid = false,
  autoUploadAfterExtract = false,
  onUploadCancelled = null,
  /** Hidden host that only opens the exists/mismatch dialog (no extract re-apply). */
  mismatchReviewOnly = false,
  /**
   * Hidden host after leaving /upload — continue extract classification + auto-save
   * without showing the upload form.
   */
  backgroundProcess = false,
  /** Tray "Continue" — claim this extracted Add Study file and finish save. */
  finishFileId = null,
} = {}) {
  const { modalities, getModality } = useModalities();
  const {
    files,
    updateFile,
    addFilesAndExtract,
    setUploadActive,
    hasPatientExistsBeenNotified,
    markPatientExistsNotified,
    registerPatientMismatchOpener,
    setTrayMinimized,
    shareTargetUsers,
    setShareTargetUsers,
  } = useUploadProgress();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { canViewDashboard } = useRbac();
  const shareTargetUsersRef = useRef([]);
  shareTargetUsersRef.current = shareTargetUsers;
  const navigateAfterStandaloneUpload = useCallback(() => {
    const from = location.state?.from;
    const returnTo = typeof from === "string" && from && !from.startsWith("/upload")
      ? from
      : (canViewDashboard ? "/" : "/patients");
    navigate(returnTo);
  }, [canViewDashboard, location.state?.from, navigate]);
  const preselectedUhidFromQuery = searchParams.get("uhid") ?? location.state?.patient?.uhid ?? "";
  const preselectedPatientFromLocation = location.state?.patient ?? null;
  const preselectedUhid = embeddedUhid ?? preselectedUhidFromQuery;
  const preselectedPatient = embeddedPatient ?? preselectedPatientFromLocation;
  const embeddedAutoUpload = Boolean(autoUploadAfterExtract);
  // Patients → Add Study: offer every modality. Standalone /upload stays CT-only.
  const isPatientStudyUpload = Boolean(embeddedUhid) || embeddedAutoUpload;
  const uploadModalities = useMemo(
    () => {
      if (isPatientStudyUpload) return modalities;
      return UPLOAD_MODALITY_KEYS
        .map((key) => modalities.find((m) => m.key === key))
        .filter(Boolean);
    },
    [modalities, isPatientStudyUpload],
  );
  const hasPreselectedPatient = Boolean(preselectedUhidFromQuery || preselectedPatientFromLocation);
  const shouldHidePatientControls = Boolean(hidePatientControls) || hasPreselectedPatient;
  const shouldHideStudyControls = Boolean(hideStudyControls) || hasPreselectedPatient;
  const shouldLockPatientUhid = Boolean(lockPatientUhid);
  const [uploading, setUploading] = useState(false);
  const [zippingFolder, setZippingFolder] = useState(false);
  const appliedExtractIdsRef = useRef(new Set());
  // Embedded Add Study owns only files picked in this sheet — never the /upload queue.
  const sessionFileIdsRef = useRef(new Set());
  // Extra ids claimed when reopening Add Study to review a tray mismatch (ref alone won't re-render).
  const [adoptedSessionFileIds, setAdoptedSessionFileIds] = useState([]);
  /** Standalone /upload: include Add Study mismatch files in session while reviewing. */
  const [reviewSessionFileIds, setReviewSessionFileIds] = useState([]);
  const isAddStudyQueueFile = useCallback((f) => (
    f?.queue === "add_study"
    || Boolean(f?.locked_patient_uhid)
    || f?.mismatch_kind === "locked"
    || f?.mismatch_kind === "add_study_new"
    || f?.mismatch_kind === "add_study_exists"
  ), []);
  const sessionFiles = useMemo(() => {
    if (embeddedAutoUpload) {
      return files.filter((f) => (
        sessionFileIdsRef.current.has(f.id)
        || adoptedSessionFileIds.includes(f.id)
      ));
    }
    // Standalone /upload must not inherit Add Study extracts into the form — except the
    // file currently opened from the tray for mismatch review.
    return files.filter((f) => (
      !isAddStudyQueueFile(f) || reviewSessionFileIds.includes(f.id)
    ));
  }, [files, embeddedAutoUpload, adoptedSessionFileIds, reviewSessionFileIds, isAddStudyQueueFile]);
  const trackSessionFiles = useCallback((items) => {
    if (!embeddedAutoUpload || !items?.length) return;
    items.forEach((item) => {
      if (item?.id) sessionFileIdsRef.current.add(item.id);
    });
  }, [embeddedAutoUpload]);
  const claimSessionFile = useCallback((fileId) => {
    if (!embeddedAutoUpload || !fileId) return;
    sessionFileIdsRef.current.add(fileId);
    setAdoptedSessionFileIds((prev) => (prev.includes(fileId) ? prev : [...prev, fileId]));
    const existing = files.find((f) => f.id === fileId);
    // /upload-origin "exists" mismatches must stay on the upload queue. Stamping
    // add_study here made Create new disappear when reopening Mismatch on a patient page.
    if (!existing || existing.mismatch_kind === "exists" || existing.mismatch_kind === "new") return;
    if (!isAddStudyQueueFile(existing)) return;
    const lockedUhid = preselectedPatient?.uhid || preselectedUhid || "";
    updateFile(fileId, {
      queue: "add_study",
      ...(lockedUhid ? { locked_patient_uhid: lockedUhid } : {}),
    });
  }, [
    embeddedAutoUpload,
    updateFile,
    preselectedPatient?.uhid,
    preselectedUhid,
    files,
    isAddStudyQueueFile,
  ]);
  const [patientUhid, setPatientUhid] = useState(preselectedUhid);
  const [extractedPatient, setExtractedPatient] = useState(null);
  const [patientMode, setPatientMode] = useState(preselectedUhid ? "existing" : "new");
  const [newPatientForm, setNewPatientForm] = useState(EMPTY_PATIENT_FORM);
  /** Standalone /upload: only show New Patient form when extract is incomplete or mismatched. */
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);
  /** After files go to the tray, keep /upload blank for the next drop. */
  const [uploadHandedOffToTray, setUploadHandedOffToTray] = useState(Boolean(backgroundProcess));
  const [creatingPatient, setCreatingPatient] = useState(false);
  const standaloneAutoSaveAttemptedRef = useRef(false);
  const [modality, setModality] = useState("");
  /** Bump to remount modality Select after reset (Radix keeps prior value if only cleared). */
  const [modalitySelectKey, setModalitySelectKey] = useState(0);
  const [echoMeasurementValues, setEchoMeasurementValues] = useState(() => (
    emptyEchoMeasurementValues("")
  ));
  const [savingEchoForm, setSavingEchoForm] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const [studies, setStudies] = useState([]);
  const [studiesLoading, setStudiesLoading] = useState(false);
  const [selectedStudyId, setSelectedStudyId] = useState("");
  const patientAnatomyPrefill = useMemo(
    () => anatomyFromPatient(preselectedPatient),
    [
      preselectedPatient?.anatomyDetails?.anatomy_type_1,
      preselectedPatient?.anatomyDetails?.anatomy_type_2,
      preselectedPatient?.anatomyDetails?.anatomy_type_3,
      preselectedPatient?.anatomy_details?.anatomy_type_1,
      preselectedPatient?.anatomy_details?.anatomy_type_2,
      preselectedPatient?.anatomy_details?.anatomy_type_3,
    ],
  );
  const [newStudy, setNewStudy] = useState({
    description: "",
    remarks: "",
    ...EMPTY_ANATOMY_FIELDS,
    ...(isPatientStudyUpload ? (patientAnatomyPrefill || {}) : {}),
    ct_reviewer: "",
  });
  const [patientMismatchOpen, setPatientMismatchOpen] = useState(false);
  /** Bumped when CT reviewer / Other comment blurs so auto-save can run after typing. */
  const [ctReviewerCommitTick, setCtReviewerCommitTick] = useState(0);
  const [commentCommitTick, setCommentCommitTick] = useState(0);
  const [patientMismatches, setPatientMismatches] = useState([]);
  const [mismatchExistingPatient, setMismatchExistingPatient] = useState(null);
  const [mismatchExtractDetails, setMismatchExtractDetails] = useState(null);
  const [mismatchFileNames, setMismatchFileNames] = useState([]);
  /** "locked" = Add Study vs this patient; "exists" = /upload matched system patient; "new" = /upload unmatched */
  const [mismatchReviewKind, setMismatchReviewKind] = useState("");
  /** True when the mismatched file came from Patients → Add Study (not /upload). */
  const [mismatchFromAddStudy, setMismatchFromAddStudy] = useState(false);
  /** Locked patient UHID for Add Study "Upload anyway". */
  const [mismatchLockedPatientUhid, setMismatchLockedPatientUhid] = useState("");
  /** Show new-patient fields inside the mismatch dialog (no navigate away). */
  const [mismatchShowNewPatientForm, setMismatchShowNewPatientForm] = useState(false);
  const patientMismatchConfirmedRef = useRef(false);

  const modalityConfig = getModality(modality);
  // Anatomy / CT reviewer for 3Mensio Working CT and CT Report — not Raw CT Image.
  const requiresCtStudyFields = modalityRequiresCtStudyFields(modality);
  const isOtherModality = modalityIsOther(modality);
  const acceptsZip = modalityConfig?.accepted_ext?.some((e) => e.toLowerCase() === ".zip");
  const zipOnly = acceptsOnlyZip(modalityConfig);
  const isReportPdf = modalityConfig?.view_mode === "pdf";

  useEffect(() => {
    setEchoMeasurementValues(emptyEchoMeasurementValues(modality));
  }, [modality]);

  useEffect(() => {
    setCommentCommitTick(0);
    setNewStudy((s) => {
      const needsCtFields = modalityRequiresCtStudyFields(modality);
      const hasAnatomy = Boolean(s.anatomy_type_1)
        || Boolean(s.anatomy_type_2)
        || Boolean(s.anatomy_type_3)
        || Boolean(String(s.ct_reviewer ?? "").trim());
      // Keep patient anatomy while modality is empty so CT fields are ready on first select.
      const clearAnatomy = Boolean(modality) && !needsCtFields && hasAnatomy;
      const prefillAnatomy = needsCtFields
        && isPatientStudyUpload
        && !s.anatomy_type_1
        && Boolean(patientAnatomyPrefill?.anatomy_type_1);
      const clearComment = !modalityIsOther(modality) && Boolean(String(s.remarks ?? "").trim());
      if (!clearAnatomy && !prefillAnatomy && !clearComment) return s;
      return {
        ...s,
        ...(clearAnatomy ? { ...EMPTY_ANATOMY_FIELDS, ct_reviewer: "" } : {}),
        ...(prefillAnatomy ? { ...EMPTY_ANATOMY_FIELDS, ...patientAnatomyPrefill } : {}),
        ...(clearComment ? { remarks: "" } : {}),
      };
    });
  }, [modality, isPatientStudyUpload, patientAnatomyPrefill]);

  // Drop a restored/queued modality that is no longer offered in the upload dropdown.
  useEffect(() => {
    if (isPatientStudyUpload) return;
    if (modality && !UPLOAD_MODALITY_KEYS.includes(modality)) {
      setModality("");
    }
  }, [modality, isPatientStudyUpload]);

  // Context tracks extract/upload file statuses; also cover local zip + final save.
  const hasLocalBusy = uploading || zippingFolder || creatingPatient;
  useEffect(() => {
    if (hasLocalBusy) setUploadActive(true);
  }, [hasLocalBusy, setUploadActive]);

  useEffect(() => {
    setSelectedStudyId("");
    setStudies([]);
    if (!patientUhid) return;
    setStudiesLoading(true);
    axios.get(`${API}/studies`, { params: { patient_uhid: patientUhid, limit: 50 } })
      .then(res => setStudies(res.data.studies || []))
      .catch(() => {})
      .finally(() => setStudiesLoading(false));
  }, [patientUhid]);

  const validateAndBuildItem = useCallback((file, config) => {
    const extError = getValidationError(config, file.name);
    if (extError) {
      toast.error(extError);
      return null;
    }
    const sizeError = getSizeValidationError(config, file.size);
    if (sizeError) {
      toast.error(sizeError);
      return null;
    }
    return {
      file,
      id: Math.random().toString(36).slice(2),
      name: file.name,
      size: file.size,
      type: inferFileType(file.name),
      progress: 0,
      status: "pending",
    };
  }, []);

  const resetStandaloneUploadForm = useCallback(() => {
    if (embeddedAutoUpload || shouldLockPatientUhid) return;
    setUploadHandedOffToTray(true);
    setModality("");
    setModalitySelectKey((key) => key + 1);
    setPatientUhid(preselectedUhid || "");
    setExtractedPatient(null);
    setPatientMode(preselectedUhid ? "existing" : "new");
    setNewPatientForm(EMPTY_PATIENT_FORM);
    setShowNewPatientForm(false);
    setSelectedStudyId("");
    setNewStudy({ description: "", remarks: "", ...EMPTY_ANATOMY_FIELDS, ct_reviewer: "" });
    setDragOver(false);
    setShareTargetUsers([]);
  }, [embeddedAutoUpload, shouldLockPatientUhid, preselectedUhid]);

  const buildQueuedStudyFields = useCallback((study = newStudy) => {
    const fields = {};
    const description = String(study?.description ?? "").trim();
    if (description) fields.description = description;
    const remarks = String(study?.remarks ?? "").trim();
    if (remarks) fields.remarks = remarks;
    if (study?.anatomy_type_1) fields.anatomy_type_1 = study.anatomy_type_1;
    if (study?.anatomy_type_2) fields.anatomy_type_2 = normalizeAnatomyType2(study.anatomy_type_2);
    if (study?.anatomy_type_3) fields.anatomy_type_3 = study.anatomy_type_3;
    const reviewer = String(study?.ct_reviewer ?? "").trim();
    if (reviewer) fields.ct_reviewer = reviewer;
    return fields;
  }, [newStudy]);

  const resolveStudyFieldsForFile = useCallback((file, fallbackStudy = newStudy) => {
    const cleanedFallback = {};
    if (fallbackStudy) {
      const description = String(fallbackStudy.description ?? "").trim();
      if (description) cleanedFallback.description = description;
      const remarks = String(fallbackStudy.remarks ?? "").trim();
      if (remarks) cleanedFallback.remarks = remarks;
      if (fallbackStudy.anatomy_type_1) cleanedFallback.anatomy_type_1 = fallbackStudy.anatomy_type_1;
      if (fallbackStudy.anatomy_type_2) cleanedFallback.anatomy_type_2 = normalizeAnatomyType2(fallbackStudy.anatomy_type_2);
      if (fallbackStudy.anatomy_type_3) cleanedFallback.anatomy_type_3 = fallbackStudy.anatomy_type_3;
      const reviewer = String(fallbackStudy.ct_reviewer ?? "").trim();
      if (reviewer) cleanedFallback.ct_reviewer = reviewer;
    }
    return {
      description: "",
      remarks: "",
      ...EMPTY_ANATOMY_FIELDS,
      ct_reviewer: "",
      ...(anatomyFromExtractDetails(file?.anatomy_details) || {}),
      ...cleanedFallback,
      ...(file?.study_fields || {}),
    };
  }, [newStudy]);

  const resolveStudyFieldsForFileRef = useRef(resolveStudyFieldsForFile);
  resolveStudyFieldsForFileRef.current = resolveStudyFieldsForFile;

  const addFiles = useCallback((newFiles) => {
    if (!modality) {
      toast.error("Select a modality");
      return;
    }
    const config = getModality(modality);
    const items = Array.from(newFiles).flatMap((f) => {
      const item = validateAndBuildItem(f, config);
      return item ? [item] : [];
    });
    if (items.length > 0) {
      trackSessionFiles(items);
      // Queue lives in UploadProgressContext so extract continues after leaving /upload
      addFilesAndExtract(items, modality, {
        queue: embeddedAutoUpload ? "add_study" : "upload",
        locked_patient_uhid: embeddedAutoUpload
          ? (preselectedPatient?.uhid || preselectedUhid || "")
          : "",
        locked_patient: embeddedAutoUpload ? preselectedPatient : null,
        study_fields: buildQueuedStudyFields(),
        share_target_users: snapshotShareTargetUsers(shareTargetUsersRef.current),
      });
      if (!embeddedAutoUpload && !shouldLockPatientUhid) {
        standaloneAutoSaveAttemptedRef.current = false;
        toast.message(
          items.length === 1 ? "Upload has started" : `Upload started for ${items.length} files`,
        );
        resetStandaloneUploadForm();
        navigateAfterStandaloneUpload();
      } else if (embeddedAutoUpload && !isEchoFormModality(modality) && typeof onUploadCancelled === "function") {
        toast.message(
          items.length === 1 ? "Upload has started" : `Upload started for ${items.length} files`,
        );
        onUploadCancelled();
      }
    }
  }, [
    modality,
    getModality,
    validateAndBuildItem,
    addFilesAndExtract,
    trackSessionFiles,
    embeddedAutoUpload,
    shouldLockPatientUhid,
    preselectedPatient,
    preselectedUhid,
    resetStandaloneUploadForm,
    buildQueuedStudyFields,
    navigateAfterStandaloneUpload,
    onUploadCancelled,
  ]);

  const handleFolderSelect = useCallback(async (e) => {
    const fileList = e.target.files;
    if (!fileList?.length) return;
    e.target.value = "";

    const config = getModality(modality);
    if (!config) return;

    if (!acceptsZip) {
      toast.error(`${config.label} does not accept folder uploads`);
      return;
    }

    setZippingFolder(true);
    try {
      const zip = new JSZip();
      for (const file of fileList) {
        zip.file(file.webkitRelativePath || file.name, file);
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const folderName = fileList[0]?.webkitRelativePath?.split("/")?.[0] ?? "folder";
      const zipFile = new File([blob], `${folderName}.zip`, { type: "application/zip" });
      const item = validateAndBuildItem(zipFile, config);
      if (item) {
        trackSessionFiles([item]);
        addFilesAndExtract([item], modality, {
          queue: embeddedAutoUpload ? "add_study" : "upload",
          locked_patient_uhid: embeddedAutoUpload
            ? (preselectedPatient?.uhid || preselectedUhid || "")
            : "",
          locked_patient: embeddedAutoUpload ? preselectedPatient : null,
          study_fields: buildQueuedStudyFields(),
          share_target_users: snapshotShareTargetUsers(shareTargetUsersRef.current),
        });
        if (!embeddedAutoUpload && !shouldLockPatientUhid) {
          standaloneAutoSaveAttemptedRef.current = false;
          toast.message("Upload has started");
          resetStandaloneUploadForm();
          navigateAfterStandaloneUpload();
        } else if (embeddedAutoUpload && !isEchoFormModality(modality) && typeof onUploadCancelled === "function") {
          toast.message("Upload has started");
          onUploadCancelled();
        }
      } else {
        toast.error(getValidationError(config, zipFile.name) ?? "Invalid file for this modality");
      }
    } catch {
      toast.error("Failed to zip folder");
    } finally {
      setZippingFolder(false);
    }
  }, [
    modality,
    getModality,
    acceptsZip,
    validateAndBuildItem,
    addFilesAndExtract,
    trackSessionFiles,
    embeddedAutoUpload,
    shouldLockPatientUhid,
    preselectedPatient,
    preselectedUhid,
    resetStandaloneUploadForm,
    buildQueuedStudyFields,
    navigateAfterStandaloneUpload,
    onUploadCancelled,
  ]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const buildNewStudyPayload = (uhid = patientUhid, options = {}) => {
    const activeModality = options.modality || modality;
    const studyFields = options.studyFields || newStudy;
    const needsCt = modalityRequiresCtStudyFields(activeModality);
    const trimmedDescription = String(studyFields.description ?? "").trim();
    const remarks = String(studyFields.remarks ?? "").trim();
    const payload = {
      patient_uhid: uhid,
      modality: activeModality,
      description: trimmedDescription,
      remarks,
      study_date: new Date().toISOString().slice(0, 10),
    };
    if (options.mismatched === true) {
      payload.mismatched = true;
    }
    if (needsCt) {
      Object.assign(payload, buildAnatomyPayload(studyFields));
      payload.ct_reviewer = studyFields.ct_reviewer?.trim() || "";
    }
    return payload;
  };

  const validateCtStudyFields = (options = {}) => {
    const activeModality = options.modality || modality;
    const studyFields = options.studyFields || newStudy;
    if (!modalityRequiresCtStudyFields(activeModality) || selectedStudyId) return true;
    const error = validateAnatomyTypes(studyFields);
    if (error) {
      toast.error(error);
      return false;
    }
    if (!studyFields.ct_reviewer?.trim()) {
      toast.error("Enter CT reviewer name");
      return false;
    }
    return true;
  };

  const getStudyUploadError = (uhid = patientUhid, options = {}) => {
    const activeModality = options.modality || modality;
    const studyFields = options.studyFields || newStudy;
    if (selectedStudyId) return null;
    if (!uhid?.trim()) {
      return "Select a patient before creating a new study";
    }
    if (modalityRequiresCtStudyFields(activeModality)) {
      const error = validateAnatomyTypes(studyFields);
      if (error) return `${error} before creating a new study`;
      if (!studyFields.ct_reviewer?.trim()) {
        return "Enter CT reviewer name before creating a new study";
      }
    }
    return null;
  };

  const resolveStudyId = async (uhid = patientUhid, options = {}) => {
    if (selectedStudyId) {
      return selectedStudyId;
    }
    if (!uhid) return null;
    if (!validateCtStudyFields(options)) return null;
    const res = await axios.post(`${API}/studies`, buildNewStudyPayload(uhid, options));
    return res.data.study?.id || res.data.id || null;
  };

  const getStudyDescription = (studyId) => {
    if (!selectedStudyId) return newStudy.description.trim();
    return studies.find((s) => s.id === studyId)?.description || "";
  };

  const createPatientFromForm = async ({ silent = false, form = null } = {}) => {
    const payload = form ?? newPatientForm;
    const missing = getMissingPatientFormFields(payload);
    if (missing.length > 0) {
      if (!mismatchShowNewPatientForm && !uploadHandedOffToTray) setShowNewPatientForm(true);
      toast.error(
        missing.length === 1
          ? `Missing patient detail: ${missing[0]}`
          : `Missing patient details: ${missing.join(", ")}`,
      );
      return null;
    }

    setCreatingPatient(true);
    try {
      const res = await axios.post(`${API}/patients`, {
        ...payload,
        age: parseInt(payload.age, 10),
      });
      const createdPatient = res.data?.patient ?? res.data;
      const uhid = createdPatient?.uhid;
      if (!uhid) {
        toast.error("Patient created but no UHID returned");
        return null;
      }
      if (!uploadHandedOffToTray) {
        setPatientUhid(uhid);
        setExtractedPatient(createdPatient);
        setPatientMode("existing");
      }
      setShowNewPatientForm(false);
      if (!silent) toast.success("Patient created successfully");
      return uhid;
    } catch (e) {
      if (!mismatchShowNewPatientForm && !uploadHandedOffToTray) setShowNewPatientForm(true);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create patient");
      return null;
    } finally {
      setCreatingPatient(false);
    }
  };

  const resolvePatientUhid = async (options = {}) => {
    if (patientUhid?.trim()) return patientUhid.trim();
    if (patientMode !== "new") return null;
    return createPatientFromForm(options);
  };

  const isUploadableFile = (f) => (
    f.status === "pending" || f.status === "extracted" || f.status === "error"
  );

  const shareStudyIfNeeded = async (studyId, filesForShare = sessionFiles) => {
    const fromFiles = (filesForShare || [])
      .flatMap((file) => shareTargetsFromFile(file));
    const fromQueue = files.flatMap((file) => shareTargetsFromFile(file));
    const users = snapshotShareTargetUsers([
      ...shareTargetUsersRef.current,
      ...fromFiles,
      ...fromQueue,
    ]);
    if (!studyId || users.length === 0) return;

    const sharedNames = [];
    for (const user of users) {
      try {
        await axios.post(`${API}/studies/${encodeURIComponent(studyId)}/share`, {
          user_id: user.id,
        });
        sharedNames.push(user.name || user.email || "user");
      } catch (e) {
        toast.error(
          formatApiErrorDetail(e.response?.data?.detail)
          || `Study saved but sharing with ${user.name || user.email || "user"} failed`,
        );
      }
    }
    if (sharedNames.length === 1) {
      toast.success(`Shared with ${sharedNames[0]}`);
    } else if (sharedNames.length > 1) {
      toast.success(`Shared with ${sharedNames.join(", ")}`);
    }
  };

  const handleSave = async (options = {}) => {
    const uploadableFiles = sessionFiles.filter(isUploadableFile);
    if (uploadableFiles.length === 0) {
      toast.error("No files to upload");
      return false;
    }

    const activeModality = options.modality || modality;
    const studyFields = options.studyFields || newStudy;
    const studyOptions = { modality: activeModality, studyFields };
    if (options.mismatched === true) studyOptions.mismatched = true;
    const needsCt = modalityRequiresCtStudyFields(activeModality);

    if (!activeModality) {
      toast.error("Select a modality");
      return false;
    }

    const failedExtractFiles = uploadableFiles.filter((f) => f.status === "error" && !f.draft_id);
    const draftFiles = uploadableFiles.filter((f) => f.draft_id);
    const directFiles = uploadableFiles.filter((f) => !f.draft_id);
    const needsStudyForUpload = draftFiles.length > 0
      || (needsCt && directFiles.length > 0);
    const needsPatient = needsStudyForUpload || failedExtractFiles.length > 0;

    let activePatientUhid = options.patientUhid?.trim() || patientUhid?.trim() || "";
    if (!activePatientUhid && patientMode === "new") {
      activePatientUhid = (
        await createPatientFromForm({ silent: true, form: options.form ?? newPatientForm })
      ) || "";
    }

    if (needsPatient && !activePatientUhid) {
      if (patientMode === "new") {
        // createPatientFromForm already toasted missing fields / API errors.
        if (!uploadHandedOffToTray) setShowNewPatientForm(true);
      } else {
        toast.error(
          draftFiles.length === 0
            ? "Select patient first"
            : "Select a patient to upload extracted files",
        );
      }
      return false;
    }

    if (needsStudyForUpload) {
      const studyError = getStudyUploadError(activePatientUhid, studyOptions);
      if (studyError) {
        toast.error(studyError);
        return false;
      }
    }

    // Locked/embedded upload: pause for tray review when extract is not this patient.
    if (
      (shouldLockPatientUhid || embeddedAutoUpload)
      && !patientMismatchConfirmedRef.current
    ) {
      const currentPatient = preselectedPatient ?? { uhid: patientUhid };
      const needsReview = uploadableFiles.filter((f) => {
        if (modalityIsOther(f?.modality)) return false;
        if (!f.patient_details) return false;
        return !isSamePatientAsLocked(currentPatient, f.patient_details);
      });
      if (needsReview.length > 0) {
        needsReview.forEach((f) => {
          if (!f?.id) return;
          const lockedUhid = currentPatient?.uhid || patientUhid || "";
          const details = f.patient_details;
          const isExistingOther = details?.exists === true
            && !isSamePatientAsLocked(currentPatient, details);
          updateFile(f.id, {
            patient_mismatch: true,
            mismatch_kind: isExistingOther ? "add_study_exists" : "add_study_new",
            mismatch_patient_uhid: isExistingOther
              ? String(details.system_uhid || "").trim() || lockedUhid
              : lockedUhid,
            queue: "add_study",
            locked_patient_uhid: lockedUhid,
          });
        });
        setTrayMinimized(false);
        toast.message(
          needsReview.length === 1
            ? "Patient mismatch — open Mismatch in the upload panel to review"
            : `${needsReview.length} files have patient mismatches — review in the upload panel`,
          { id: UPLOAD_EXTRACT_TOAST_ID },
        );
        return false;
      }
    }
    patientMismatchConfirmedRef.current = false;

    setUploading(true);

    let resolvedStudyId = selectedStudyId || null;
    const reportPdf = getModality(activeModality)?.view_mode === "pdf";

    if (!selectedStudyId && activePatientUhid) {
      if (!validateCtStudyFields(studyOptions)) {
        setUploading(false);
        return false;
      }
      try {
        resolvedStudyId = await resolveStudyId(activePatientUhid, studyOptions);
      } catch (err) {
        const detail = formatApiErrorDetail(err?.response?.data?.detail) || "Failed to create study";
        toast.error(detail);
        setUploading(false);
        return false;
      }
    }

    if (needsStudyForUpload && !resolvedStudyId) {
      toast.error("Could not resolve a study — select an existing study or try again");
      setUploading(false);
      return false;
    }

    let uploadSuccessCount = 0;

    for (const item of draftFiles) {
      updateFile(item.id, { status: "uploading", progress: 20, phase: "saving" });
      try {
        if (reportPdf) {
          await axios.post(`${API}/studies/${encodeURIComponent(resolvedStudyId)}/import-report`, {
            draft_id: item.draft_id,
          });
        } else {
          await axios.post(`${API}/upload`, {
            draft_id: item.draft_id,
            patient_uhid: activePatientUhid,
            study_id: resolvedStudyId,
            modality: activeModality,
          });
        }
        updateFile(item.id, {
          status: "completed",
          progress: 100,
          phase: "done",
          uploaded_patient_uhid: activePatientUhid || "",
        });
        uploadSuccessCount += 1;
      } catch (err) {
        const detail = formatApiErrorDetail(err?.response?.data?.detail)
          || (reportPdf ? "Report import failed" : "Upload failed");
        toast.error(detail);
        updateFile(item.id, { status: "extracted", progress: 0, phase: "ready" });
      }
    }

    for (const item of directFiles) {
      updateFile(item.id, { status: "uploading", progress: 20, phase: "saving" });
      try {
        const formData = new FormData();
        formData.append("file", item.file);
        if (activePatientUhid) formData.append("patient_uhid", activePatientUhid);
        if (resolvedStudyId) formData.append("study_id", resolvedStudyId);
        formData.append("modality", activeModality);
        formData.append("file_type", item.type);
        await axios.post(`${API}/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (p) => {
            const pct = Math.round((p.loaded * 100) / (p.total ?? 1));
            updateFile(item.id, { progress: pct });
          },
        });
        updateFile(item.id, {
          status: "completed",
          progress: 100,
          phase: "done",
          uploaded_patient_uhid: activePatientUhid || "",
        });
        uploadSuccessCount += 1;
      } catch (err) {
        const detail = formatApiErrorDetail(err?.response?.data?.detail)
          || (err?.response?.status === 415 ? "File type not accepted for this modality" : "Upload failed");
        toast.error(detail);
        updateFile(item.id, { status: "error", progress: 0, phase: "error", error: detail });
      }
    }

    setUploading(false);

    if (uploadSuccessCount > 0) {
      if (!options.silent) toast.success("Saved successfully");
      if (resolvedStudyId) {
        await shareStudyIfNeeded(resolvedStudyId);
      }
      // Close Add Study / mismatch host and refresh the patient studies list.
      if (!options.skipClose && embeddedAutoUpload && typeof onUploadCancelled === "function") {
        onUploadCancelled();
      }
    }

    return uploadSuccessCount > 0;
  };

  const todayIsoDate = () => new Date().toISOString().slice(0, 10);

  const saveEchoFormStudy = async ({ study_date, measurements }) => {
    const activePatientUhid = String(patientUhid || preselectedUhid || "").trim();
    if (!activePatientUhid) {
      toast.error("Select a patient before creating a study");
      return false;
    }
    if (!isEchoFormModality(modality)) {
      toast.error("Select Pre-Echo or Post-Echo");
      return false;
    }

    const res = await axios.post(`${API}/studies`, {
      patient_uhid: activePatientUhid,
      study_date,
      modality,
      fileAvailable: false,
      measurements,
    });
    const studyId = res.data?.study?.id || res.data?.id || null;
    if (studyId) await shareStudyIfNeeded(studyId);
    return true;
  };

  const handleEchoCombinedSave = async () => {
    const { measurements } = buildEchoMeasurements(modality, echoMeasurementValues, {
      partial: true,
    });
    const wantFile = sessionFiles.some(isUploadableFile);
    const wantForm = measurements.length > 0;

    if (!wantForm && !wantFile) {
      toast.error("Enter measurements or add a file");
      return false;
    }

    setSavingEchoForm(true);
    let formOk = false;
    let fileOk = false;
    try {
      if (wantForm) {
        try {
          formOk = await saveEchoFormStudy({
            study_date: todayIsoDate(),
            measurements,
          });
        } catch (err) {
          toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to save study");
        }
      }
      if (wantFile) {
        fileOk = await handleSave({
          silent: wantForm,
          skipClose: true,
        });
      }

      if (formOk && fileOk) {
        toast.success("Saved two studies");
      } else if (formOk) {
        toast.success("Study saved");
      }

      if ((formOk || fileOk) && typeof onUploadCancelled === "function") {
        onUploadCancelled();
      }
      return formOk || fileOk;
    } finally {
      setSavingEchoForm(false);
    }
  };

  const isSaving = uploading || creatingPatient || savingEchoForm;
  const isExtracting = sessionFiles.some((f) => (
    f.status === "uploading"
    && (f.phase === "uploading" || f.phase === "extracting" || !f.phase)
  ));

  // Apply DICOM patient/anatomy details when extraction finishes (incl. after returning to page).
  useEffect(() => {
    if (mismatchReviewOnly) return;
    sessionFiles.forEach((f) => {
      if (f.status !== "extracted") return;
      if (appliedExtractIdsRef.current.has(f.id)) return;

      // Already classified for tray review — never re-stamp when the file is reopened
      // on another page (e.g. Add Study Mismatch opened from /upload).
      if (f.patient_mismatch && f.mismatch_kind) {
        appliedExtractIdsRef.current.add(f.id);
        return;
      }

      appliedExtractIdsRef.current.add(f.id);

      const patientDetails = f.patient_details;
      const isAddStudyFile = f.origin === "add_study" || isAddStudyQueueFile(f);

      if (!shouldLockPatientUhid) {
        if (!uploadHandedOffToTray && !isAddStudyFile) {
          applyExtractedPatientDetails(patientDetails, {
            setPatientMode,
            setPatientUhid,
            setExtractedPatient,
            setNewPatientForm,
          });
        }
        // Never rewrite Add Study files as /upload "Already exists".
        if (patientDetails?.exists === true && !isAddStudyFile) {
          // Prefill can re-run after navigate-back; only evaluate the exists review once per draft.
          if (!hasPatientExistsBeenNotified(f.id)) {
            markPatientExistsNotified(f.id);
            const systemUhid = patientDetails.system_uhid || "";

            const markExistsReviewIfNeeded = (existing) => {
              if (!existing?.uhid) {
                // System said exists but we couldn't load the record — treat as incomplete new patient.
                if (uploadHandedOffToTray) {
                  updateFile(f.id, standaloneNewPatientMismatchPatch());
                  setTrayMinimized(false);
                  toast.message(
                    "Extracted patient is new — open Mismatch to create a patient and save",
                    { id: UPLOAD_EXTRACT_TOAST_ID },
                  );
                  return;
                }
                const formFromExtract = newPatientFormFromExtractDetails(patientDetails);
                const missing = getMissingPatientFormFields(formFromExtract);
                if (!uploadHandedOffToTray) setShowNewPatientForm(true);
                if (missing.length > 0) {
                  toast.error(
                    missing.length === 1
                      ? `Missing patient detail: ${missing[0]} — complete the form to save`
                      : `Missing patient details: ${missing.join(", ")} — complete the form to save`,
                    { id: UPLOAD_EXTRACT_TOAST_ID },
                  );
                } else {
                  toast.message(
                    "Could not load existing patient — review details and save",
                    { id: UPLOAD_EXTRACT_TOAST_ID },
                  );
                }
                return;
              }
              setShowNewPatientForm(false);
              // Always require review via the upload panel, even when fields match.
              updateFile(f.id, {
                patient_mismatch: true,
                mismatch_kind: "exists",
                mismatch_patient_uhid: existing.uhid,
                queue: "upload",
                origin: "upload",
                locked_patient_uhid: "",
              });
              setTrayMinimized(false);
              toast.message(
                "Patient already exists — open Already exists in the upload panel to continue",
                { id: UPLOAD_EXTRACT_TOAST_ID },
              );
            };

            if (systemUhid) {
              axios.get(`${API}/patients/${encodeURIComponent(systemUhid)}`)
                .then((res) => {
                  const existing = res.data?.patient ?? res.data;
                  if (existing?.uhid) {
                    setExtractedPatient((prev) => (
                      prev?.uhid === existing.uhid ? { ...prev, ...existing } : prev
                    ));
                    markExistsReviewIfNeeded(existing);
                    return;
                  }
                  markExistsReviewIfNeeded(null);
                })
                .catch(() => {
                  markExistsReviewIfNeeded(null);
                });
            } else {
              markExistsReviewIfNeeded(null);
            }
          }
        }
      } else if (patientDetails) {
        setExtractedPatient(identityFromExtractDetails(patientDetails));
      }

      if (!uploadHandedOffToTray && modalityRequiresCtStudyFields(f.modality || modality)) {
        const extractedAnatomy = anatomyFromExtractDetails(f.anatomy_details);
        if (extractedAnatomy?.anatomy_type_1) {
          setNewStudy((s) => ({
            ...s,
            ...EMPTY_ANATOMY_FIELDS,
            ...extractedAnatomy,
          }));
        }
      }

      const lockedPatient = preselectedPatient ?? { uhid: patientUhid };
      const lockedUhid = lockedPatient?.uhid || patientUhid || "";

      if (shouldLockPatientUhid || embeddedAutoUpload) {
        // Never reclassify /upload-origin files as Add Study — that flips the tray
        // from Already exists → Mismatch and swaps the modal actions.
        if (
          f.mismatch_kind === "exists"
          || f.queue === "upload"
          || f.origin === "upload"
        ) {
          return;
        }
        // Add Study cases:
        // 3) same patient → no modal, auto-save
        // 2) different existing → Upload anyway + existing (no Create new)
        // 1) new → Upload anyway + Create new patient
        if (modalityIsOther(f.modality) || isSamePatientAsLocked(lockedPatient, patientDetails)) {
          updateFile(f.id, {
            patient_mismatch: false,
            mismatch_kind: "",
            mismatch_patient_uhid: "",
            queue: "add_study",
            locked_patient_uhid: lockedUhid,
          });
          toast.success(
            modalityIsOther(f.modality)
              ? "Saving to this patient"
              : "Extraction complete — patient matched",
            { id: UPLOAD_EXTRACT_TOAST_ID },
          );
        } else if (patientDetails?.exists === true) {
          const systemUhid = String(patientDetails.system_uhid || "").trim();
          const alreadyFlagged = Boolean(f.patient_mismatch && f.mismatch_kind === "add_study_exists");
          updateFile(f.id, {
            patient_mismatch: true,
            mismatch_kind: "add_study_exists",
            mismatch_patient_uhid: systemUhid || lockedUhid,
            queue: "add_study",
            locked_patient_uhid: lockedUhid,
          });
          if (!alreadyFlagged) {
            setTrayMinimized(false);
            toast.message(
              "Extracted patient already exists — open Mismatch to choose where to save",
              { id: UPLOAD_EXTRACT_TOAST_ID },
            );
          }
        } else {
          const alreadyFlagged = Boolean(f.patient_mismatch && f.mismatch_kind === "add_study_new");
          updateFile(f.id, {
            patient_mismatch: true,
            mismatch_kind: "add_study_new",
            mismatch_patient_uhid: lockedUhid,
            queue: "add_study",
            locked_patient_uhid: lockedUhid,
          });
          if (!alreadyFlagged) {
            setTrayMinimized(false);
            toast.message(
              "Extracted patient is new — open Mismatch to create or upload anyway",
              { id: UPLOAD_EXTRACT_TOAST_ID },
            );
          }
        }
      } else if (patientDetails?.exists === true && !shouldLockPatientUhid) {
        // Standalone /upload exists review is handled above (async); don't clear flags here.
        if (!isAddStudyFile) setShowNewPatientForm(false);
      } else if (!isAddStudyFile) {
        // New / unknown patient on standalone /upload.
        if (needsStandaloneNewPatientReview({
          ...f,
          origin: f.origin || "upload",
          queue: f.queue || "upload",
          patient_details: patientDetails,
        })) {
          const alreadyFlagged = Boolean(f.patient_mismatch && f.mismatch_kind === "new");
          updateFile(f.id, standaloneNewPatientMismatchPatch());
          if (!alreadyFlagged) {
            setTrayMinimized(false);
            toast.message(
              "Extracted patient is new — open Mismatch to create a patient and save",
              { id: UPLOAD_EXTRACT_TOAST_ID },
            );
          }
          return;
        }
        updateFile(f.id, { patient_mismatch: false, mismatch_kind: "", mismatch_patient_uhid: "" });
        setShowNewPatientForm(false);
        // Allow standalone auto-save effect to create patient + study.
        standaloneAutoSaveAttemptedRef.current = false;
        toast.success(
          "Extraction complete — patient details ready",
          { id: UPLOAD_EXTRACT_TOAST_ID },
        );
      }
    });
  }, [
    sessionFiles,
    modality,
    shouldLockPatientUhid,
    embeddedAutoUpload,
    preselectedPatient,
    patientUhid,
    hasPatientExistsBeenNotified,
    markPatientExistsNotified,
    updateFile,
    setTrayMinimized,
    isAddStudyQueueFile,
    uploadHandedOffToTray,
    mismatchReviewOnly,
  ]);

  // Clear extract toasts when leaving the upload page
  useEffect(() => () => {
    toast.dismiss(UPLOAD_EXTRACT_TOAST_ID);
  }, []);

  // Add Study must not inherit toasts / pause messages from the /upload queue.
  useEffect(() => {
    if (!embeddedAutoUpload) return undefined;
    toast.dismiss(UPLOAD_EXTRACT_TOAST_ID);
    return () => {
      toast.dismiss(UPLOAD_EXTRACT_TOAST_ID);
    };
  }, [embeddedAutoUpload]);

  // Tray / file X cancelled the queue — close exists/mismatch dialogs (and Add Study via callback).
  const hadSessionWorkRef = useRef(false);
  useEffect(() => {
    if (mismatchReviewOnly) return;
    if (sessionFiles.length > 0) {
      hadSessionWorkRef.current = true;
      return;
    }
    setPatientMismatchOpen(false);
    setPatientMismatches([]);
    setMismatchExistingPatient(null);
    setMismatchExtractDetails(null);
    setMismatchFileNames([]);
    setMismatchReviewKind("");
    setMismatchFromAddStudy(false);
    setMismatchLockedPatientUhid("");
    setMismatchShowNewPatientForm(false);
    setShowNewPatientForm(false);
    setNewPatientForm(EMPTY_PATIENT_FORM);
    standaloneAutoSaveAttemptedRef.current = false;
    patientMismatchConfirmedRef.current = false;
    if (hadSessionWorkRef.current && typeof onUploadCancelled === "function") {
      hadSessionWorkRef.current = false;
      onUploadCancelled();
    }
  }, [sessionFiles, onUploadCancelled, mismatchReviewOnly]);

  // In embedded mode from `PatientDetail`, we don't show patient/study UI or a manual "Save" button.
  // Instead, once we have extracted draft results, we automatically trigger the same upload/create flow
  // (after the user reviews a patient mismatch from the upload tray when details don't match).
  const isEmbeddedUpload = embeddedAutoUpload;
  const autoUploadAttemptedRef = useRef(false);
  const filesRef = useRef(files);
  filesRef.current = files;
  const finishClaimedRef = useRef(null);

  // Reopen from tray: claim the ready file, restore modality / study fields, allow auto-save.
  useEffect(() => {
    if (!isEmbeddedUpload || mismatchReviewOnly) return;
    const lockedUhid = String(preselectedPatient?.uhid || preselectedUhid || "").trim();

    const adoptReadyFile = (file) => {
      if (!file?.id) return;
      claimSessionFile(file.id);
      if (file.modality) setModality(file.modality);
      const restored = resolveStudyFieldsForFileRef.current(file, null);
      setNewStudy((s) => ({ ...s, ...restored }));
      autoUploadAttemptedRef.current = false;
      setCtReviewerCommitTick(0);
      setCommentCommitTick(0);
      if (modalityRequiresCtStudyFields(file.modality || "")) {
        const fields = { ...restored, ...(file.study_fields || {}) };
        const anatomyError = validateAnatomyTypes(fields);
        const missingReviewer = !String(fields.ct_reviewer ?? "").trim();
        if (anatomyError || missingReviewer) {
          updateFile(file.id, { needs_study_details: true });
        }
      }
    };

    if (finishFileId) {
      if (finishClaimedRef.current === finishFileId) return;
      const finishFile = files.find((f) => f.id === finishFileId) ?? null;
      if (!finishFile) return;
      finishClaimedRef.current = finishFileId;
      adoptReadyFile(finishFile);
      return;
    }

    if (!lockedUhid) return;
    files.forEach((f) => {
      if (!f?.id || sessionFileIdsRef.current.has(f.id)) return;
      if (!(f.origin === "add_study" || f.queue === "add_study")) return;
      if (String(f.locked_patient_uhid || "").trim() !== lockedUhid) return;
      if (f.status === "completed" || f.status === "error") return;
      adoptReadyFile(f);
    });
  }, [
    isEmbeddedUpload,
    mismatchReviewOnly,
    finishFileId,
    files,
    claimSessionFile,
    preselectedPatient?.uhid,
    preselectedUhid,
    updateFile,
  ]);

  // Leaving Add Study before save completes — keep tray actionable.
  useEffect(() => {
    if (!isEmbeddedUpload) return undefined;
    return () => {
      const lockedUhid = String(preselectedPatient?.uhid || preselectedUhid || "").trim();
      filesRef.current.forEach((f) => {
        if (!f?.id) return;
        if (!(sessionFileIdsRef.current.has(f.id) || f.id === finishFileId)) return;
        if (f.status !== "extracted" || f.patient_mismatch) return;
        if (!modalityRequiresCtStudyFields(f.modality || "")) return;
        const fields = f.study_fields || {};
        const anatomyError = validateAnatomyTypes(fields);
        const missingReviewer = !String(fields.ct_reviewer ?? "").trim();
        if (anatomyError || missingReviewer) {
          updateFile(f.id, {
            needs_study_details: true,
            locked_patient_uhid: f.locked_patient_uhid || lockedUhid,
            queue: f.queue || "add_study",
            origin: f.origin || "add_study",
          });
        }
      });
    };
  }, [
    isEmbeddedUpload,
    finishFileId,
    preselectedPatient?.uhid,
    preselectedUhid,
    updateFile,
  ]);

  const clearSessionPatientMismatchFlags = useCallback(() => {
    sessionFiles.forEach((f) => {
      if (f?.id && f.patient_mismatch) {
        updateFile(f.id, {
          patient_mismatch: false,
          mismatch_kind: "",
          mismatch_patient_uhid: "",
        });
      }
    });
  }, [sessionFiles, updateFile]);

  const openMismatchModalForFile = useCallback(async (fileId) => {
    const file = files.find((f) => f.id === fileId) ?? null;
    if (!file) return;
    const isStandaloneNewKind = (
      file.mismatch_kind === "new"
      || (
        file.origin === "upload"
        && file.patient_details?.exists !== true
        && file.mismatch_kind !== "exists"
      )
    );
    if (!file.patient_details && !isStandaloneNewKind) {
      toast.message("No extracted patient details available for this file");
      return;
    }

    // Allow a fresh review even if a prior confirm action left the flag set.
    patientMismatchConfirmedRef.current = false;

    // Re-opened Add Study starts with an empty session set — claim this file so save works.
    if (file.id) claimSessionFile(file.id);
    // Restore modality + study fields so mismatch retry can edit anatomy / CT reviewer.
    if (file.modality) {
      setModality(file.modality);
    }
    const restoredStudy = resolveStudyFieldsForFileRef.current(file, null);
    setNewStudy((s) => ({
      ...s,
      ...restoredStudy,
    }));

    const patientDetails = file.patient_details || {};
    // Heal /upload files that were wrongly stamped as Add Study when mismatch was
    // reopened from a patient page (keeps Create new + "Existing patient" UI).
    if (
      file.origin === "upload"
      && (file.mismatch_kind === "exists" || file.patient_details?.exists === true)
      && (file.queue === "add_study" || file.locked_patient_uhid)
    ) {
      updateFile(file.id, {
        queue: "upload",
        locked_patient_uhid: "",
        mismatch_kind: "exists",
        mismatch_patient_uhid: file.mismatch_patient_uhid || patientDetails?.system_uhid || "",
      });
    }
    if (
      file.origin === "upload"
      && file.mismatch_kind === "new"
      && (file.queue === "add_study" || file.locked_patient_uhid)
    ) {
      updateFile(file.id, standaloneNewPatientMismatchPatch());
    }
    // Keep Add Study files as Add Study even when the tray is opened from /upload.
    if (
      !embeddedAutoUpload
      && !shouldLockPatientUhid
      && file.id
      && (file.origin === "add_study" || isAddStudyQueueFile(file))
    ) {
      setReviewSessionFileIds((prev) => (prev.includes(file.id) ? prev : [...prev, file.id]));
    }
    // Dialog copy follows the *file origin*, not the page you are on.
    const fromAddStudy = Boolean(
      file.origin === "add_study"
      || file.mismatch_kind === "add_study_exists"
      || file.mismatch_kind === "add_study_new"
      || file.mismatch_kind === "locked"
    );
    setMismatchFromAddStudy(fromAddStudy);

    const fetchPatientByUhid = async (uhid) => {
      const id = String(uhid || "").trim();
      if (!id) return null;
      try {
        const res = await axios.get(`${API}/patients/${encodeURIComponent(id)}`);
        return res.data?.patient ?? res.data ?? null;
      } catch {
        return null;
      }
    };

    const resolveLockedPatient = async () => {
      // Prefer the patient stored on the file (Add Study target), not whatever page
      // we happen to be on — mismatch_patient_uhid may point at a different existing match.
      const lockedUhid = String(file.locked_patient_uhid || "").trim();
      if (lockedUhid) {
        if (preselectedPatient?.uhid === lockedUhid) return preselectedPatient;
        const fetched = await fetchPatientByUhid(lockedUhid);
        if (fetched?.uhid) return fetched;
        return { uhid: lockedUhid };
      }
      if (preselectedPatient?.uhid) return preselectedPatient;
      if (patientUhid) return { uhid: patientUhid };
      return null;
    };

    // ——— Patients → Add Study ———
    if (fromAddStudy) {
      const lockedPatient = await resolveLockedPatient();
      if (!lockedPatient?.uhid) {
        toast.message("Could not load the patient you are uploading for");
        return;
      }

      // Case 3: same patient — no modal (only when not already flagged for review).
      // If the tray Mismatch button was clicked, always show the modal.
      if (
        !file.patient_mismatch
        && isSamePatientAsLocked(lockedPatient, patientDetails)
      ) {
        updateFile(file.id, {
          patient_mismatch: false,
          mismatch_kind: "",
          mismatch_patient_uhid: "",
        });
        toast.success("Patient details match this patient");
        return;
      }

      setMismatchLockedPatientUhid(lockedPatient.uhid);
      setPatientMismatches(buildPatientCompareRows(lockedPatient, patientDetails));
      setMismatchExtractDetails(patientDetails);
      setMismatchFileNames([file.name].filter(Boolean));
      setMismatchShowNewPatientForm(false);

      const isExistingOther = file.mismatch_kind === "add_study_exists"
        || file.mismatch_kind === "exists"
        || (
          patientDetails?.exists === true
          && !isSamePatientAsLocked(lockedPatient, patientDetails)
        );

      if (isExistingOther) {
        // Case 2: different existing patient
        const systemUhid = String(
          patientDetails.system_uhid || file.mismatch_patient_uhid || "",
        ).trim();
        const existing = await fetchPatientByUhid(systemUhid);
        if (!existing?.uhid) {
          // Fall back to new-patient actions if we can't load the existing record.
          setMismatchExistingPatient(null);
          setMismatchReviewKind("add_study_new");
        } else {
          setMismatchExistingPatient(existingPatientFromExtractDetails(patientDetails) || {
            uhid: existing.uhid,
            name: existing.name || "",
            displayUhid: formatPatientDisplayUhid(existing),
          });
          setMismatchReviewKind("add_study_exists");
        }
      } else {
        // Case 1: new patient
        setMismatchExistingPatient(null);
        setMismatchReviewKind("add_study_new");
      }

      setPatientMismatchOpen(true);
      setTrayMinimized(false);
      return;
    }

    // ——— Standalone /upload ———
    // Only true /upload "Already exists" — never Add Study kinds (immutable by origin).
    const isExistsReview = (
      file.origin !== "add_study"
      && !isAddStudyQueueFile(file)
      && (
        file.mismatch_kind === "exists"
        || (
          file.origin === "upload"
          && !shouldLockPatientUhid
          && !embeddedAutoUpload
          && patientDetails?.exists === true
        )
      )
    );

    if (isExistsReview) {
      const systemUhid = String(
        patientDetails.system_uhid || file.mismatch_patient_uhid || "",
      ).trim();

      const existing = await fetchPatientByUhid(systemUhid);
      if (!existing?.uhid) {
        updateFile(file.id, {
          patient_mismatch: false,
          mismatch_kind: "",
          mismatch_patient_uhid: "",
        });
        toast.message("Could not load the existing patient record");
        return;
      }

      setPatientUhid(existing.uhid);
      setPatientMode("existing");
      setPatientMismatches(buildPatientCompareRows(existing, patientDetails));
      setMismatchExtractDetails(patientDetails);
      setMismatchExistingPatient(existingPatientFromExtractDetails(patientDetails) || {
        uhid: existing.uhid,
        name: existing.name || "",
        displayUhid: formatPatientDisplayUhid(existing),
      });
      setMismatchFileNames([file.name].filter(Boolean));
      setMismatchLockedPatientUhid("");
      setMismatchReviewKind("exists");
      setMismatchShowNewPatientForm(false);
      setPatientMismatchOpen(true);
      setTrayMinimized(false);
      return;
    }

    const isNewPatientReview = (
      file.origin !== "add_study"
      && !isAddStudyQueueFile(file)
      && (
        file.mismatch_kind === "new"
        || (
          file.origin === "upload"
          && !shouldLockPatientUhid
          && !embeddedAutoUpload
          && patientDetails?.exists !== true
        )
      )
    );

    if (isNewPatientReview) {
      setPatientMode("new");
      setPatientUhid("");
      setPatientMismatches(buildPatientCompareRows(null, patientDetails));
      setMismatchExtractDetails({ ...patientDetails, exists: false });
      setMismatchExistingPatient(null);
      setMismatchFileNames([file.name].filter(Boolean));
      setMismatchLockedPatientUhid("");
      setMismatchReviewKind("new");
      setMismatchShowNewPatientForm(false);
      setPatientMismatchOpen(true);
      setTrayMinimized(false);
      return;
    }

    const currentPatient = preselectedPatient ?? { uhid: patientUhid };
    const mismatches = getPatientDetailMismatches(currentPatient, patientDetails);
    if (mismatches.length === 0) {
      updateFile(file.id, {
        patient_mismatch: false,
        mismatch_kind: "",
        mismatch_patient_uhid: "",
      });
      toast.success("Patient details match this patient");
      return;
    }

    setPatientMismatches(buildPatientCompareRows(currentPatient, patientDetails));
    setMismatchExtractDetails(patientDetails);
    setMismatchExistingPatient(existingPatientFromExtractDetails(patientDetails));
    setMismatchFileNames([file.name].filter(Boolean));
    setMismatchLockedPatientUhid("");
    setMismatchReviewKind("locked");
    setMismatchShowNewPatientForm(false);
    setPatientMismatchOpen(true);
    setTrayMinimized(false);
  }, [
    files,
    claimSessionFile,
    shouldLockPatientUhid,
    embeddedAutoUpload,
    preselectedPatient,
    patientUhid,
    updateFile,
    setTrayMinimized,
    isAddStudyQueueFile,
  ]);

  useEffect(() => {
    // Register on both /upload and Add Study so tray Mismatch opens the shared review modal.
    return registerPatientMismatchOpener(openMismatchModalForFile);
  }, [registerPatientMismatchOpener, openMismatchModalForFile]);

  useEffect(() => {
    if (!isEmbeddedUpload) return;
    if (autoUploadAttemptedRef.current) return;
    if (!modality) return;
    if (isEchoFormModality(modality)) return;
    if (isSaving || isExtracting || zippingFolder) return;
    if (patientMismatchOpen) return;

    const hasMismatch = sessionFiles.some((f) => f.patient_mismatch);
    if (hasMismatch) {
      // Wait for the user to review via the upload tray Mismatch button.
      return;
    }

    const hasExtractedDraft = sessionFiles.some((f) => (
      f.status === "extracted"
      && f.draft_id
    ));
    if (!hasExtractedDraft) return;

    const readyFile = sessionFiles.find((f) => (
      f.status === "extracted"
      && f.draft_id
      && !f.patient_mismatch
    ));
    if (!readyFile) return;

    const studyFields = resolveStudyFieldsForFile(readyFile, newStudy);
    const fileModality = readyFile?.modality || modality;
    const needsCt = requiresCtStudyFields || modalityRequiresCtStudyFields(fileModality);
    const needsOtherComment = false;

    if (needsCt) {
      const anatomyError = validateAnatomyTypes(studyFields);
      if (anatomyError) {
        if (readyFile.id && !readyFile.needs_study_details) {
          updateFile(readyFile.id, { needs_study_details: true });
        }
        toast.error(`${anatomyError} to finish saving`, { id: UPLOAD_EXTRACT_TOAST_ID });
        return;
      }
      if (!studyFields.ct_reviewer?.trim()) {
        if (readyFile.id && !readyFile.needs_study_details) {
          updateFile(readyFile.id, { needs_study_details: true });
        }
        toast.error("Enter CT reviewer name to finish saving", { id: UPLOAD_EXTRACT_TOAST_ID });
        window.requestAnimationFrame(() => {
          document.querySelector('[data-testid="upload-ct-reviewer"]')?.focus?.();
        });
        return;
      }
    }

    if (needsOtherComment && readyFile.id && !readyFile.needs_study_details) {
      updateFile(readyFile.id, { needs_study_details: true });
    }

    // User was typing study details (e.g. CT reviewer / Other comment) — wait until they pause or blur
    // so we don't save mid-input and close the sheet.
    const waitingOnStudyDetails = (needsCt && ctReviewerCommitTick === 0)
      || (needsOtherComment && commentCommitTick === 0);
    const saveDelayMs = (
      (readyFile.needs_study_details && waitingOnStudyDetails)
      || (needsOtherComment && commentCommitTick === 0)
    ) ? 1500 : 0;
    const timer = window.setTimeout(() => {
      if (autoUploadAttemptedRef.current) return;

      if (readyFile.id && readyFile.needs_study_details) {
        updateFile(readyFile.id, {
          needs_study_details: false,
          study_fields: buildQueuedStudyFields(studyFields),
        });
      }

      autoUploadAttemptedRef.current = true;
      handleSave({
        modality: fileModality || undefined,
        studyFields,
      }).then((ok) => {
        if (ok === false) autoUploadAttemptedRef.current = false;
      }).catch(() => {
        autoUploadAttemptedRef.current = false;
      });
    }, saveDelayMs);

    return () => window.clearTimeout(timer);
  }, [
    isEmbeddedUpload,
    modality,
    sessionFiles,
    isSaving,
    isExtracting,
    zippingFolder,
    requiresCtStudyFields,
    isOtherModality,
    newStudy,
    patientMismatchOpen,
    resolveStudyFieldsForFile,
    updateFile,
    buildQueuedStudyFields,
    ctReviewerCommitTick,
    commentCommitTick,
  ]);

  // Standalone /upload: when new-patient extract is complete, create patient + save study automatically.
  useEffect(() => {
    if (mismatchReviewOnly) return;
    if (isEmbeddedUpload || shouldLockPatientUhid) return;
    if (standaloneAutoSaveAttemptedRef.current) return;
    if (showNewPatientForm) return;
    if (!uploadHandedOffToTray && patientMode !== "new") return;
    if (!uploadHandedOffToTray && !modality) return;
    if (isSaving || isExtracting || zippingFolder) return;
    if (patientMismatchOpen) return;

    const hasMismatch = sessionFiles.some((f) => f.patient_mismatch);
    if (hasMismatch) return;

    const readyFile = sessionFiles.find((f) => (
      f.status === "extracted"
      && f.draft_id
      && f.patient_details
      && f.patient_details.exists !== true
    ));
    if (!readyFile) return;

    const fileModality = modality || readyFile.modality || "";
    if (!fileModality) return;

    const form = uploadHandedOffToTray
      ? newPatientFormFromExtractDetails({ ...readyFile.patient_details, exists: false })
      : newPatientForm;
    const missing = getMissingPatientFormFields(form);
    if (missing.length > 0) {
      if (readyFile.id) {
        updateFile(readyFile.id, standaloneNewPatientMismatchPatch());
        setTrayMinimized(false);
      }
      if (!uploadHandedOffToTray) setShowNewPatientForm(true);
      toast.message(
        "Extracted patient is new — open Mismatch to create a patient and save",
        { id: UPLOAD_EXTRACT_TOAST_ID },
      );
      return;
    }

    const studyFields = resolveStudyFieldsForFile(readyFile, uploadHandedOffToTray ? null : newStudy);

    if (modalityRequiresCtStudyFields(fileModality)) {
      const anatomyError = validateAnatomyTypes(studyFields);
      if (anatomyError || !studyFields.ct_reviewer?.trim()) {
        if (readyFile.id) {
          updateFile(readyFile.id, standaloneNewPatientMismatchPatch());
          setTrayMinimized(false);
        }
        toast.message(
          "Extracted patient is new — open Mismatch to enter study details and save",
          { id: UPLOAD_EXTRACT_TOAST_ID },
        );
        return;
      }
    }

    standaloneAutoSaveAttemptedRef.current = true;
    toast.success("Saving new patient and study…", { id: UPLOAD_EXTRACT_TOAST_ID });
    handleSave({
      modality: fileModality,
      studyFields,
      form,
    }).catch(() => {
      standaloneAutoSaveAttemptedRef.current = false;
      if (!uploadHandedOffToTray) setShowNewPatientForm(true);
    });
  }, [
    mismatchReviewOnly,
    isEmbeddedUpload,
    shouldLockPatientUhid,
    showNewPatientForm,
    patientMode,
    modality,
    sessionFiles,
    isSaving,
    isExtracting,
    zippingFolder,
    patientMismatchOpen,
    newPatientForm,
    requiresCtStudyFields,
    newStudy,
    uploadHandedOffToTray,
    resolveStudyFieldsForFile,
    updateFile,
    setTrayMinimized,
  ]);

  const resetMismatchDialogState = () => {
    setMismatchExistingPatient(null);
    setMismatchExtractDetails(null);
    setMismatchFileNames([]);
    setMismatchReviewKind("");
    setMismatchFromAddStudy(false);
    setMismatchLockedPatientUhid("");
    setMismatchShowNewPatientForm(false);
  };

  const getMismatchReadyFile = () => (
    sessionFiles.find((f) => (
      f.patient_mismatch
      || f.modality
      || f.anatomy_details
      || f.study_fields
    )) || null
  );

  const persistMismatchStudyFields = (nextStudy) => {
    const readyFile = getMismatchReadyFile();
    if (!readyFile?.id) return;
    updateFile(readyFile.id, {
      study_fields: buildQueuedStudyFields(nextStudy),
    });
  };

  const handleMismatchStudyFieldChange = (patch) => {
    setNewStudy((s) => {
      const next = { ...s, ...patch };
      persistMismatchStudyFields(next);
      return next;
    });
  };

  const handleConfirmPatientMismatch = () => {
    const lockedUhid = mismatchLockedPatientUhid.trim();
    const readyFile = getMismatchReadyFile();
    const fileModality = readyFile?.modality || modality || "";
    const studyFields = resolveStudyFieldsForFile(readyFile, newStudy);
    setNewStudy((s) => ({ ...s, ...studyFields }));

    if (modalityRequiresCtStudyFields(fileModality)) {
      const anatomyError = validateAnatomyTypes(studyFields);
      if (anatomyError) {
        toast.error(`${anatomyError} before creating a new study`);
        setPatientMismatchOpen(true);
        return;
      }
      if (!studyFields.ct_reviewer?.trim()) {
        toast.error("Enter CT reviewer name before creating a new study");
        setPatientMismatchOpen(true);
        return;
      }
    }

    patientMismatchConfirmedRef.current = true;
    autoUploadAttemptedRef.current = true;

    handleSave(lockedUhid
      ? {
        patientUhid: lockedUhid,
        modality: fileModality || undefined,
        studyFields,
        mismatched: true,
      }
      : { modality: fileModality || undefined, studyFields, mismatched: true })
      .then((ok) => {
        if (!ok) {
          patientMismatchConfirmedRef.current = false;
          setPatientMismatchOpen(true);
          return;
        }
        setPatientMismatchOpen(false);
        resetMismatchDialogState();
        clearSessionPatientMismatchFlags();
        patientMismatchConfirmedRef.current = false;
        if (typeof onUploadCancelled === "function") onUploadCancelled();
      })
      .catch(() => {
        patientMismatchConfirmedRef.current = false;
        setPatientMismatchOpen(true);
      });
  };

  const handleUploadToMismatchExistingPatient = () => {
    const existingUhid = mismatchExistingPatient?.uhid?.trim();
    if (!existingUhid) return;

    const readyFile = getMismatchReadyFile();
    const fileModality = readyFile?.modality || modality || "";
    const studyFields = resolveStudyFieldsForFile(readyFile, newStudy);
    setNewStudy((s) => ({ ...s, ...studyFields }));

    if (modalityRequiresCtStudyFields(fileModality)) {
      const anatomyError = validateAnatomyTypes(studyFields);
      if (anatomyError) {
        toast.error(`${anatomyError} before creating a new study`);
        setPatientMismatchOpen(true);
        return;
      }
      if (!studyFields.ct_reviewer?.trim()) {
        toast.error("Enter CT reviewer name before creating a new study");
        setPatientMismatchOpen(true);
        return;
      }
    }

    patientMismatchConfirmedRef.current = true;
    autoUploadAttemptedRef.current = true;
    setPatientUhid(existingUhid);
    setPatientMode("existing");

    handleSave({
      patientUhid: existingUhid,
      modality: fileModality || undefined,
      studyFields,
    })
      .then((ok) => {
        if (!ok) {
          patientMismatchConfirmedRef.current = false;
          setPatientMismatchOpen(true);
          return;
        }
        setPatientMismatchOpen(false);
        resetMismatchDialogState();
        clearSessionPatientMismatchFlags();
        patientMismatchConfirmedRef.current = false;
        if (typeof onUploadCancelled === "function") onUploadCancelled();
        if (embeddedAutoUpload || shouldLockPatientUhid) {
          navigate(`/patients/${encodeURIComponent(existingUhid)}`);
        }
      })
      .catch(() => {
        patientMismatchConfirmedRef.current = false;
        setPatientMismatchOpen(true);
      });
  };

  const handleCreateNewFromMismatch = () => {
    // Prefill from extract (prefer DICOM id over system match) and keep dialog open.
    const formSource = { ...mismatchExtractDetails, exists: false };
    const form = newPatientFormFromExtractDetails(formSource);
    setNewPatientForm(form);
    setPatientMode("new");
    setPatientUhid("");
    setShowNewPatientForm(false);
    setMismatchShowNewPatientForm(true);
  };

  const handleSubmitMismatchNewPatient = () => {
    const form = newPatientForm;
    const missing = getMissingPatientFormFields(form);
    if (missing.length > 0) {
      toast.error(
        missing.length === 1
          ? `Missing patient detail: ${missing[0]}`
          : `Missing patient details: ${missing.join(", ")}`,
      );
      return;
    }

    const readyFile = getMismatchReadyFile();
    const fileModality = readyFile?.modality || modality || "";
    const studyFields = resolveStudyFieldsForFile(readyFile, newStudy);
    setNewStudy((s) => ({ ...s, ...studyFields }));

    if (modalityRequiresCtStudyFields(fileModality)) {
      const anatomyError = validateAnatomyTypes(studyFields);
      if (anatomyError) {
        toast.error(`${anatomyError} before creating a new study`);
        return;
      }
      if (!studyFields.ct_reviewer?.trim()) {
        toast.error("Enter CT reviewer name before creating a new study");
        return;
      }
    }

    patientMismatchConfirmedRef.current = true;
    autoUploadAttemptedRef.current = true;
    setPatientMode("new");
    setPatientUhid("");

    createPatientFromForm({ silent: true, form })
      .then((createdUhid) => {
        if (!createdUhid) {
          patientMismatchConfirmedRef.current = false;
          setPatientMismatchOpen(true);
          setMismatchShowNewPatientForm(true);
          return null;
        }
        return handleSave({
          patientUhid: createdUhid,
          modality: fileModality || undefined,
          studyFields,
          form,
        }).then((ok) => (
          ok ? createdUhid : null
        ));
      })
      .then((createdUhid) => {
        if (!createdUhid) {
          // Stay on the in-dialog form so the user can fix and retry.
          patientMismatchConfirmedRef.current = false;
          setPatientMismatchOpen(true);
          setMismatchShowNewPatientForm(true);
          return;
        }
        setPatientMismatchOpen(false);
        resetMismatchDialogState();
        clearSessionPatientMismatchFlags();
        patientMismatchConfirmedRef.current = false;
        if (typeof onUploadCancelled === "function") onUploadCancelled();
        if (embeddedAutoUpload || shouldLockPatientUhid) {
          navigate(`/patients/${encodeURIComponent(createdUhid)}`);
        }
      })
      .catch(() => {
        patientMismatchConfirmedRef.current = false;
        setPatientMismatchOpen(true);
        setMismatchShowNewPatientForm(true);
      });
  };

  const handleCancelPatientMismatch = () => {
    if (patientMismatchConfirmedRef.current) return;
    patientMismatchConfirmedRef.current = false;
    setPatientMismatchOpen(false);
    resetMismatchDialogState();
    // Keep attempted=true so the embedded auto-upload effect does not
    // immediately re-open this dialog after cancel (stuck-modal loop).
    autoUploadAttemptedRef.current = true;
    toast.message("Upload paused — open Mismatch in the upload panel when ready to review", {
      id: UPLOAD_EXTRACT_TOAST_ID,
    });
    // Unmount mismatch review host (Add Study or global exists host); keep flags for tray retry.
    if (typeof onUploadCancelled === "function") {
      window.queueMicrotask(() => onUploadCancelled());
    }
  };

  const isAddStudyNewReview = mismatchReviewKind === "add_study_new";
  const isAddStudyExistsReview = mismatchReviewKind === "add_study_exists";
  const isStandaloneNewReview = mismatchReviewKind === "new";
  const mismatchIsNewPatient = isAddStudyNewReview
    || isStandaloneNewReview
    || mismatchExtractDetails?.exists === false;
  const mismatchHasFieldDiffs = patientMismatches.some(patientCompareValuesDiffer);
  const isExistsReviewModal = mismatchReviewKind === "exists";
  // Column label: patient-page host = this patient; /upload = existing match.
  const mismatchLeftColumnLabel = mismatchFromAddStudy ? "This patient" : "Existing patient";
  // Add Study (patient page):
  // 1) new → Upload to This Patient + Create new
  // 2) other existing → Upload to This Patient + Upload to Existing (no Create new)
  // /upload exists → Upload to Existing Patient + Create new (no Upload to This Patient)
  const existingMismatchUhid = String(mismatchExistingPatient?.uhid || "").trim();
  const lockedMismatchUhid = String(mismatchLockedPatientUhid || "").trim();
  const existingIsDifferentPatient = Boolean(
    existingMismatchUhid
    && lockedMismatchUhid
    && existingMismatchUhid !== lockedMismatchUhid,
  );
  const showUploadAnyway = mismatchFromAddStudy
    || (
      !isExistsReviewModal
      && !isAddStudyExistsReview
      && !isAddStudyNewReview
      && !isStandaloneNewReview
    );
  // Create new: Add Study only when no existing match; /upload for exists/new reviews.
  const showCreateNewPatient = mismatchFromAddStudy
    ? isAddStudyNewReview
    : (isExistsReviewModal || mismatchIsNewPatient);
  const showUploadToExisting = mismatchFromAddStudy
    ? isAddStudyExistsReview && existingIsDifferentPatient
    : Boolean(mismatchExistingPatient);
  const mismatchReadyFile = getMismatchReadyFile();
  const mismatchModality = mismatchReadyFile?.modality || modality || "";
  const mismatchNeedsCtFields = modalityRequiresCtStudyFields(mismatchModality);
  // Keep study fields mounted after the user fills them — hiding on value
  // unmounted the anatomy dropdown and CT reviewer input mid-edit.
  const showMismatchAnatomy1 = mismatchNeedsCtFields;
  const showMismatchAnatomy2 = mismatchNeedsCtFields
    && Boolean(newStudy.anatomy_type_1)
    && needsAnatomyType2(newStudy.anatomy_type_1);
  const showMismatchAnatomy3 = mismatchNeedsCtFields
    && Boolean(newStudy.anatomy_type_1)
    && Boolean(newStudy.anatomy_type_2)
    && needsAnatomyType3(newStudy.anatomy_type_1);
  const showMismatchCtReviewer = mismatchNeedsCtFields;
  const showMismatchStudyFields = mismatchNeedsCtFields;
  const hasUploadableFiles = sessionFiles.some(isUploadableFile);
  const showEchoCombinedEntry = isPatientStudyUpload && isEchoFormModality(modality);
  const echoFormHasValues = !isEchoMeasurementValuesEmpty(modality, echoMeasurementValues);
  const echoSaveEnabled = echoFormHasValues || hasUploadableFiles;
  const dropDisabled = !modality;
  const acceptAttr = buildAcceptAttribute(modalityConfig?.accepted_ext);
  const formatHint = modalityConfig
    ? formatAcceptedExtensions(modalityConfig.accepted_ext)
    : "";

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 text-black" data-testid="upload-page">
      <div className="space-y-4">
      <Card
        className={
          embeddedAutoUpload
            ? "border-none bg-transparent rounded-none"
            : "border-[var(--xl-line)] bg-[var(--xl-surf)]"
        }
        data-testid="study-section"
      >
        <CardContent className="p-4 space-y-4">
          <div className="flex w-full min-w-0 flex-row flex-wrap items-end gap-3">
            <div className="space-y-1.5 min-w-0 w-44 flex-shrink-0">
              <Label className={uploadLabelClass}>Modality</Label>
              <Select
                key={modalitySelectKey}
                value={modality || undefined}
                onValueChange={setModality}
              >
                <SelectTrigger data-testid="upload-modality" className={`h-10 w-full ${uploadSelectClass}`}>
                  <SelectValue placeholder="Select modality" />
                </SelectTrigger>
                <SelectContent className={uploadSelectContentClass}>
                  {uploadModalities.map((m) => (
                    <SelectItem key={m.key} value={m.key} textValue={m.label}>
                      <ModalityDotLabel modalityKey={m.key} label={m.label} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {requiresCtStudyFields && (
              <>
                <div className="space-y-1.5 min-w-0 flex-1 sm:min-w-[9rem]">
                  <Label className={uploadLabelClass}>Anatomy Type *</Label>
                  <Select
                    value={newStudy.anatomy_type_1 || undefined}
                    onValueChange={(v) => setNewStudy((s) => ({
                      ...s,
                      anatomy_type_1: v,
                      anatomy_type_2: "",
                      anatomy_type_3: "",
                    }))}
                  >
                    <SelectTrigger data-testid="upload-anatomy-type-1" className={`h-10 w-full ${uploadSelectClass}`}>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className={uploadSelectContentClass}>
                      {ANATOMY_TYPE_1_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {needsAnatomyType2(newStudy.anatomy_type_1) && (
                  <div className="space-y-1.5 min-w-0 flex-1 sm:min-w-[9rem]">
                    <Label className={uploadLabelClass}>Anatomy Type 2 *</Label>
                    <Select
                      value={newStudy.anatomy_type_2 || undefined}
                      onValueChange={(v) => setNewStudy((s) => ({
                        ...s,
                        anatomy_type_2: v,
                        anatomy_type_3: "",
                      }))}
                    >
                      <SelectTrigger data-testid="upload-anatomy-type-2" className={`h-10 w-full ${uploadSelectClass}`}>
                        <SelectValue placeholder="Select subtype" />
                      </SelectTrigger>
                      <SelectContent className={uploadSelectContentClass}>
                        {getAnatomyType2Options(newStudy.anatomy_type_1).map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {needsAnatomyType3(newStudy.anatomy_type_1) && newStudy.anatomy_type_2 && (
                  <div className="space-y-1.5 min-w-0 flex-1 sm:min-w-[9rem]">
                    <Label className={uploadLabelClass}>Anatomy Type 3 *</Label>
                    <Select
                      value={newStudy.anatomy_type_3 || undefined}
                      onValueChange={(v) => setNewStudy((s) => ({ ...s, anatomy_type_3: v }))}
                    >
                      <SelectTrigger data-testid="upload-anatomy-type-3" className={`h-10 w-full ${uploadSelectClass}`}>
                        <SelectValue placeholder="Select manufacturer" />
                      </SelectTrigger>
                      <SelectContent className={uploadSelectContentClass}>
                        {getAnatomyType3Options(newStudy.anatomy_type_2).map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5 min-w-0 flex-1 sm:min-w-[9rem]">
                  <Label className={uploadLabelClass}>CT Reviewer *</Label>
                  <Input
                    data-testid="upload-ct-reviewer"
                    value={newStudy.ct_reviewer}
                    onChange={(e) => {
                      // Typing again — prefer idle debounce over a prior blur commit.
                      setCtReviewerCommitTick(0);
                      setNewStudy((s) => ({ ...s, ct_reviewer: e.target.value }));
                    }}
                    onBlur={() => {
                      if (!String(newStudy.ct_reviewer ?? "").trim()) return;
                      setCtReviewerCommitTick((tick) => tick + 1);
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      e.currentTarget.blur();
                    }}
                    placeholder="Enter Reviewer's name"
                    required
                    aria-required="true"
                    className={`h-10 w-full ${uploadInputClass}`}
                  />
                </div>
              </>
            )}
            {!embeddedAutoUpload && (
              <UploadShareTargetFields
                selectedUsers={shareTargetUsers}
                onSelectedUsersChange={(users) => {
                  setShareTargetUsers(snapshotShareTargetUsers(users));
                }}
              />
            )}
            {isOtherModality && (
              <div className="space-y-1.5 min-w-0 w-full basis-full">
                <Label className={uploadLabelClass}>Comment*</Label>
                <Textarea
                  data-testid="upload-other-comment"
                  value={newStudy.remarks}
                  onChange={(e) => {
                    setCommentCommitTick(0);
                    setNewStudy((s) => ({ ...s, remarks: e.target.value }));
                  }}
                  onBlur={() => setCommentCommitTick((tick) => tick + 1)}
                  placeholder="Enter Comment"
                  rows={2}
                  className={`min-h-[2.5rem] w-full resize-y ${uploadInputClass}`}
                />
              </div>
            )}
          </div>

          {showEchoCombinedEntry && (
            <EchoMeasurementForm
              modality={modality}
              values={echoMeasurementValues}
              onValuesChange={setEchoMeasurementValues}
              showSubmit={false}
              submitting={savingEchoForm}
            />
          )}

          <div
            data-testid="drop-zone"
            className={`rounded-xl border-2 border-dashed transition-colors flex flex-col items-center justify-center text-center ${
              dropDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
            } ${showEchoCombinedEntry ? "min-h-[180px] p-6 sm:p-8" : "min-h-[240px] p-8 sm:p-10"} ${
              dragOver
                ? "border-[var(--xl-primary)] bg-[var(--xl-primary-soft)]"
                : "border-[var(--xl-primary-border)] bg-[var(--xl-primary-soft)] hover:border-[var(--xl-primary)]/50"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              if (!dropDisabled) setDragOver(true);
            }}
            onDragLeave={() => {
              if (!dropDisabled) setDragOver(false);
            }}
            onDrop={(e) => {
              if (dropDisabled) return;
              handleDrop(e);
            }}
            onClick={() => {
              if (dropDisabled) return;
              if (!zippingFolder) fileInputRef.current?.click();
            }}
          >
            <div className="w-16 h-16 rounded-2xl bg-[var(--xl-primary-soft)] flex items-center justify-center mb-4">
              {zippingFolder ? (
                <Loader2 className="w-8 h-8 text-[var(--xl-primary)] animate-spin" />
              ) : (
                <CloudUpload className="w-8 h-8 text-[var(--xl-primary)]" />
              )}
            </div>
            <h3 className="text-lg font-semibold text-black mb-1">
              {zippingFolder ? "Zipping folder…" : "Drop files here"}
            </h3>
            <p className="text-sm text-black mb-4">
              {dropDisabled
                ? "Select a modality to upload files"
                : modalityConfig
                  ? `or click to browse. ${modalityConfig.label}: ${formatHint}`
                  : "or click to browse"}
            </p>
            {modalityConfig && (
              <div className="flex flex-wrap gap-2 justify-center mb-4">
                {modalityConfig.accepted_ext.map((ext) => (
                  <Badge key={ext} variant="outline" className="text-[10px] border-[var(--xl-line2)] text-black font-normal">
                    {ext.toUpperCase()}
                  </Badge>
                ))}
              </div>
            )}
            {acceptsZip && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={zippingFolder}
                onClick={(e) => {
                  e.stopPropagation();
                  folderInputRef.current?.click();
                }}
              >
                <FolderInput className="w-4 h-4" />
                {zipOnly ? "Upload DICOM Folder (ZIP)" : "Upload Folder (ZIP)"}
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              data-testid="file-input"
              onChange={(e) => {
                if (e.target.files?.length) addFiles(e.target.files);
                e.target.value = "";
              }}
              accept={acceptAttr}
            />
            <input
              ref={folderInputRef}
              type="file"
              webkitdirectory=""
              directory=""
              multiple
              className="hidden"
              data-testid="folder-input"
              onChange={handleFolderSelect}
            />
          </div>
        </CardContent>
      </Card>

      {false && (
        <Card className="border-[var(--xl-line)] bg-[var(--xl-surf)] overflow-visible" data-testid="study-patient-section">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-medium text-black">Study &amp; Patient</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-4 pb-4 space-y-4 overflow-visible">
          <div className="space-y-3 min-w-0 relative z-20">
            <Label className={uploadLabelClass}>Patient</Label>
            {shouldHidePatientControls ? (
              <Badge
                variant="outline"
                className="text-[10px] font-normal border-[var(--xl-line2)] text-black"
              >
                {formatPatientDisplayUhid(preselectedPatient ?? { uhid: patientUhid })}
              </Badge>
            ) : (
              <>
                <div className="flex gap-2 w-full sm:max-w-md">
                  <button
                    type="button"
                    data-testid="patient-mode-existing"
                    onClick={() => setPatientMode("existing")}
                    className={`flex-1 flex items-center justify-center gap-2 h-9 text-sm rounded-lg border transition-colors ${
                      patientMode === "existing"
                        ? "border-[var(--xl-primary)] bg-[var(--xl-primary-soft)] text-black"
                        : "border-[var(--xl-primary-border)] bg-[var(--xl-surf)] text-black hover:border-[var(--xl-primary)]/50 hover:bg-[var(--xl-primary-soft)]/40"
                    }`}
                  >
                    <User className="w-4 h-4" /> Existing Patient
                  </button>
                  <button
                    type="button"
                    data-testid="patient-mode-new"
                    onClick={() => {
                      setPatientMode("new");
                      setPatientUhid("");
                      setExtractedPatient(null);
                      setSelectedStudyId("");
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 h-9 text-sm rounded-lg border transition-colors ${
                      patientMode === "new"
                        ? "border-[var(--xl-primary)] bg-[var(--xl-primary-soft)] text-black"
                        : "border-[var(--xl-primary-border)] bg-[var(--xl-surf)] text-black hover:border-[var(--xl-primary)]/50 hover:bg-[var(--xl-primary-soft)]/40"
                    }`}
                  >
                    <UserPlus className="w-4 h-4" /> New Patient
                  </button>
                </div>

                {patientMode === "existing" && (
                  <div className="relative w-full">
                    <PatientCombobox
                      value={patientUhid}
                      onChange={(uhid) => {
                        setPatientUhid(uhid);
                        if (!uhid) setExtractedPatient(null);
                      }}
                      initialPatient={preselectedPatient}
                      selectedPatient={extractedPatient}
                    />
                  </div>
                )}

                {patientMode === "new" && (
                  <NewPatientFormFields
                    form={newPatientForm}
                    onChange={setNewPatientForm}
                  />
                )}
              </>
            )}
          </div>

          {(patientUhid || patientMode === "new") && (
            <>
              <Separator className="bg-[var(--xl-line)]" />
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className={uploadLabelClass}>Study</Label>
                  <Badge variant="outline" className="text-[10px] font-normal border-[var(--xl-line2)] text-black">
                    {patientUhid
                      ? formatPatientDisplayUhid(extractedPatient ?? { uhid: patientUhid })
                      : newPatientForm.external_uhid || newPatientForm.name || "New patient"}
                  </Badge>
                </div>
                {!shouldHideStudyControls && (
                  <div className={`grid grid-cols-1 gap-3 ${patientMode === "existing" ? "sm:grid-cols-2" : ""}`}>
                    <div className="space-y-1.5">
                      <Label className={uploadLabelClass}>Internal Notes</Label>
                      <Input
                        data-testid="new-study-description"
                        value={newStudy.description}
                        onChange={(e) => setNewStudy((s) => ({ ...s, description: e.target.value }))}
                        placeholder="e.g. Pre-op notes"
                        disabled={!!selectedStudyId}
                        className={`h-9 disabled:opacity-60 ${uploadInputClass}`}
                      />
                    </div>
                    {patientMode === "existing" && (
                      <div className="space-y-1.5">
                        <Label className={uploadLabelClass}>Attach to Existing</Label>
                        <Select
                          value={selectedStudyId || "__new__"}
                          onValueChange={(value) => {
                            if (value === "__new__") {
                              setSelectedStudyId("");
                              return;
                            }
                            setSelectedStudyId(value);
                            const study = studies.find((s) => s.id === value);
                            if (study?.modality) setModality(study.modality);
                          }}
                          disabled={studiesLoading}
                        >
                          <SelectTrigger data-testid="upload-existing-study" className={`h-9 w-full ${uploadSelectClass}`}>
                            <SelectValue placeholder={studiesLoading ? "Loading studies…" : "New study"} />
                          </SelectTrigger>
                          <SelectContent className={uploadSelectContentClass}>
                            <SelectItem value="__new__">New study</SelectItem>
                            {studies.map((s) => (
                              <SelectItem key={s.id} value={s.id} data-testid={`study-option-${s.id}`}>
                                <span className="truncate">
                                  {s.description || "Untitled"}
                                  {s.study_date ? ` · ${s.study_date}` : ""}
                                  {` · ${getModality(s.modality)?.label ?? s.modality}`}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!studiesLoading && studies.length === 0 && patientUhid && (
                          <p className="text-[10px] text-black">No existing studies for this patient</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="flex justify-end pt-2">
            <Button
              data-testid="upload-save-btn"
              onClick={handleSave}
              disabled={isSaving || isExtracting || zippingFolder || !hasUploadableFiles}
              size="sm"
              className="gap-2 rounded-[10px] shadow-[0_5px_14px_var(--xl-primary-shadow)]"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isSaving ? "Saving…" : "Save"}
            </Button>
          </div>
        </CardContent>
        </Card>
      )}
      {!embeddedAutoUpload && showNewPatientForm && !uploadHandedOffToTray && (
        <div className="space-y-3 border border-[var(--xl-line)] rounded-xl bg-[var(--xl-surf)] p-4">
          <Label className={uploadLabelClass}>New Patient</Label>
          <NewPatientFormFields
            form={newPatientForm}
            onChange={setNewPatientForm}
          />
          <div className="flex justify-end pt-2">
            <Button
              data-testid="upload-save-btn"
              onClick={handleSave}
              disabled={isSaving || isExtracting || zippingFolder || !hasUploadableFiles}
              size="sm"
              className="gap-2 rounded-[10px] shadow-[0_5px_14px_var(--xl-primary-shadow)]"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isSaving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
      {!embeddedAutoUpload && !showNewPatientForm && hasUploadableFiles && !uploadHandedOffToTray && (
        <div className="flex justify-end pt-1">
          <Button
            data-testid="upload-save-btn"
            onClick={handleSave}
            disabled={isSaving || isExtracting || zippingFolder}
            size="sm"
            className="gap-2 rounded-[10px] shadow-[0_5px_14px_var(--xl-primary-shadow)]"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </div>
      )}
      {embeddedAutoUpload && (showEchoCombinedEntry || hasUploadableFiles) && (
        <div className="flex justify-end pt-1">
          <Button
            data-testid="upload-save-btn-embedded"
            onClick={showEchoCombinedEntry ? handleEchoCombinedSave : handleSave}
            disabled={
              isSaving
              || isExtracting
              || zippingFolder
              || (showEchoCombinedEntry && !echoSaveEnabled)
            }
            size="sm"
            className="gap-2 rounded-full"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isSaving ? "Saving…" : "Save study"}
          </Button>
        </div>
      )}
      </div>

      <AlertDialog
        open={patientMismatchOpen}
        onOpenChange={(open) => {
          if (open) {
            setPatientMismatchOpen(true);
            return;
          }
          // Keep dialog open while a confirm/upload action is in flight or saving.
          if (patientMismatchConfirmedRef.current || isSaving) return;
          handleCancelPatientMismatch();
        }}
      >
        <AlertDialogContent
          className={
            mismatchShowNewPatientForm || showMismatchStudyFields
              ? "max-w-2xl"
              : "max-w-lg"
          }
          data-testid="patient-mismatch-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              {mismatchShowNewPatientForm
                ? "Create new patient"
                : isAddStudyNewReview || isStandaloneNewReview
                  ? "Extracted patient is new"
                  : isAddStudyExistsReview || (isExistsReviewModal && !mismatchHasFieldDiffs)
                    ? "Patient already exists"
                    : "Patient details do not match"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {mismatchShowNewPatientForm
                ? "Review or edit the patient details from the file, then create the patient and upload."
                : isStandaloneNewReview
                  ? "No matching patient was found. Create a new patient from the file details."
                  : isAddStudyNewReview
                    ? "No matching patient was found. Upload to this patient, or create a new patient from the file details."
                    : isAddStudyExistsReview
                      ? "The patient in the file already exists as a different record. Choose where to upload."
                      : isExistsReviewModal
                        ? (mismatchFromAddStudy
                          ? "This patient is already in the system. Review the patient you are uploading for against the file details before continuing."
                          : "This patient is already in the system. Review the existing record against the file details before continuing.")
                        : "File details differ from the patient you are uploading for. Review the differences below before continuing."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {mismatchFileNames.length > 0 ? (
            <div
              className="flex items-start gap-2 rounded-xl border border-[var(--xl-line)] bg-secondary/30 px-3 py-2.5 text-sm"
              data-testid="patient-mismatch-file-names"
            >
              <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {mismatchFileNames.length === 1 ? "File" : "Files"}
                </p>
                <ul className="mt-0.5 space-y-0.5">
                  {mismatchFileNames.map((name) => (
                    <li key={name} className="break-all font-medium text-black">
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}

          {mismatchShowNewPatientForm ? (
            <div
              className="rounded-xl border border-[var(--xl-line)] bg-[var(--xl-surf)] p-3 sm:p-4"
              data-testid="patient-mismatch-new-patient-form"
            >
              <NewPatientFormFields
                form={newPatientForm}
                onChange={setNewPatientForm}
                className="!grid-cols-1 sm:!grid-cols-2 lg:!grid-cols-2"
              />
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-[var(--xl-line)] overflow-hidden text-sm">
                <div className="grid grid-cols-3 gap-2 bg-secondary/50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <span>Field</span>
                  <span>{mismatchLeftColumnLabel}</span>
                  <span>Extracted</span>
                </div>
                {patientMismatches.map((row) => (
                  <div
                    key={row.field}
                    className="grid grid-cols-3 gap-2 px-3 py-2 border-t border-[var(--xl-line)]"
                  >
                    <span className="font-medium text-black">{row.field}</span>
                    <span className="break-words text-black">{row.current}</span>
                    <span
                      className={`break-words ${
                        patientCompareValuesDiffer(row)
                          ? "font-medium text-amber-800"
                          : "text-black"
                      }`}
                    >
                      {row.extracted}
                    </span>
                  </div>
                ))}
              </div>

              {mismatchExistingPatient && (isAddStudyExistsReview || isExistsReviewModal) ? (
                <div
                  className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950"
                  data-testid="patient-mismatch-exists-notice"
                >
                  <p className="font-medium">This patient already exists</p>
                  <p className="mt-0.5 text-amber-900/90">
                    {mismatchExistingPatient.name
                      ? `${mismatchExistingPatient.name} (${mismatchExistingPatient.displayUhid})`
                      : mismatchExistingPatient.displayUhid}
                    {" "}
                    is already in the system.
                    {isAddStudyExistsReview
                      ? " Upload to this patient, or upload to the existing patient."
                      : " Upload to the existing patient, or create a new patient from the file details."}
                  </p>
                </div>
              ) : null}

              {isAddStudyNewReview || isStandaloneNewReview ? (
                <div
                  className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm text-sky-950"
                  data-testid="patient-mismatch-new-notice"
                >
                  <p className="font-medium">This looks like a new patient</p>
                  <p className="mt-0.5 text-sky-900/90">
                    {isAddStudyNewReview
                      ? "No matching patient was found. Create a new patient from the file details, or upload to this patient."
                      : "No matching patient was found. Create a new patient from the file details."}
                  </p>
                </div>
              ) : null}
            </>
          )}

          {showMismatchStudyFields ? (
            <div
              className="space-y-3 rounded-xl border border-[var(--xl-line)] bg-secondary/20 px-3 py-3"
              data-testid="patient-mismatch-ct-fields"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Study details
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {showMismatchAnatomy1 ? (
                  <div className="space-y-1.5 min-w-0">
                    <Label className={uploadLabelClass}>Anatomy Type *</Label>
                    <Select
                      value={newStudy.anatomy_type_1 || undefined}
                      onValueChange={(v) => handleMismatchStudyFieldChange({
                        anatomy_type_1: v,
                        anatomy_type_2: "",
                        anatomy_type_3: "",
                      })}
                    >
                      <SelectTrigger
                        data-testid="mismatch-anatomy-type-1"
                        className={`h-10 w-full ${uploadSelectClass}`}
                      >
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className={uploadSelectContentClass}>
                        {ANATOMY_TYPE_1_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
                {showMismatchAnatomy2 ? (
                  <div className="space-y-1.5 min-w-0">
                    <Label className={uploadLabelClass}>Anatomy Type 2 *</Label>
                    <Select
                      value={newStudy.anatomy_type_2 || undefined}
                      onValueChange={(v) => handleMismatchStudyFieldChange({
                        anatomy_type_2: v,
                        anatomy_type_3: "",
                      })}
                    >
                      <SelectTrigger
                        data-testid="mismatch-anatomy-type-2"
                        className={`h-10 w-full ${uploadSelectClass}`}
                      >
                        <SelectValue placeholder="Select subtype" />
                      </SelectTrigger>
                      <SelectContent className={uploadSelectContentClass}>
                        {getAnatomyType2Options(newStudy.anatomy_type_1).map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
                {showMismatchAnatomy3 ? (
                  <div className="space-y-1.5 min-w-0">
                    <Label className={uploadLabelClass}>Anatomy Type 3 *</Label>
                    <Select
                      value={newStudy.anatomy_type_3 || undefined}
                      onValueChange={(v) => handleMismatchStudyFieldChange({
                        anatomy_type_3: v,
                      })}
                    >
                      <SelectTrigger
                        data-testid="mismatch-anatomy-type-3"
                        className={`h-10 w-full ${uploadSelectClass}`}
                      >
                        <SelectValue placeholder="Select manufacturer" />
                      </SelectTrigger>
                      <SelectContent className={uploadSelectContentClass}>
                        {getAnatomyType3Options(newStudy.anatomy_type_2).map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
                {showMismatchCtReviewer ? (
                  <div className="space-y-1.5 min-w-0">
                    <Label className={uploadLabelClass}>CT Reviewer *</Label>
                    <Input
                      data-testid="mismatch-ct-reviewer"
                      value={newStudy.ct_reviewer}
                      onChange={(e) => handleMismatchStudyFieldChange({
                        ct_reviewer: e.target.value,
                      })}
                      placeholder="Enter Reviewer's name"
                      required
                      aria-required="true"
                      className={`h-10 w-full ${uploadInputClass}`}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:space-x-0 sm:gap-2">
            {mismatchShowNewPatientForm ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="patient-mismatch-new-patient-back"
                  disabled={isSaving}
                  onClick={() => setMismatchShowNewPatientForm(false)}
                >
                  Back
                </Button>
                <AlertDialogCancel
                  data-testid="patient-mismatch-cancel"
                  disabled={isSaving}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  data-testid="patient-mismatch-create-new-submit"
                  disabled={isSaving}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmitMismatchNewPatient();
                  }}
                  className="inline-flex items-center gap-2 bg-[var(--xl-primary)] hover:bg-[var(--xl-primary-hover)]"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    "Create & Upload"
                  )}
                </AlertDialogAction>
              </>
            ) : (
              <>
                <AlertDialogCancel data-testid="patient-mismatch-cancel">
                  Cancel
                </AlertDialogCancel>
                {showUploadAnyway ? (
                  <AlertDialogAction
                    data-testid="patient-mismatch-confirm"
                    onClick={(e) => {
                      e.preventDefault();
                      handleConfirmPatientMismatch();
                    }}
                    className="bg-[var(--xl-primary)] hover:bg-[var(--xl-primary-hover)]"
                  >
                    Upload to This Patient
                  </AlertDialogAction>
                ) : null}
                {showUploadToExisting ? (
                  <AlertDialogAction
                    data-testid="patient-mismatch-upload-existing"
                    onClick={(e) => {
                      e.preventDefault();
                      handleUploadToMismatchExistingPatient();
                    }}
                    className="bg-[var(--xl-primary)] hover:bg-[var(--xl-primary-hover)]"
                  >
                    Upload to Existing Patient
                  </AlertDialogAction>
                ) : null}
                {showCreateNewPatient ? (
                  <AlertDialogAction
                    data-testid="patient-mismatch-create-new"
                    onClick={(e) => {
                      e.preventDefault();
                      handleCreateNewFromMismatch();
                    }}
                    className={
                      isExistsReviewModal || isAddStudyNewReview
                        ? "border border-[var(--xl-line2)] bg-white text-black shadow-none hover:bg-secondary/60 hover:text-black"
                        : "bg-[var(--xl-primary)] hover:bg-[var(--xl-primary-hover)]"
                    }
                  >
                    Create new patient
                  </AlertDialogAction>
                ) : null}
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
